// Live UI Pattern Reference page.
//
// This route renders working examples of the canonical UI patterns used
// throughout the app — same data shapes, same wrappers, same spacing —
// inside the org-scoped layout so theme / breadcrumb / LocaleContext apply
// the way they do on real pages. It is the live counterpart to
// docs/frontend/patterns.md and is verified by front/e2e/patterns.spec.ts.
//
// Unlinked from sidebar / nav menus — discoverable via URL or the docs
// index only, mirroring the debug.tsx precedent.
//
// Note: the spec proposed `_patterns.tsx`, but TanStack Router's file-based
// routing treats a leading-underscore file as a pathless layout route, so
// the URL would not resolve. The drop-the-underscore form preserves the
// intent (unlinked, dev-facing) while remaining a real navigable route.

import { SecretReveal } from '../../components/shared/SecretReveal'
import { formatAssetWithPath } from '../../lib/assetPath'
import { AgentRunCard } from '../../components/agent-runs/AgentRunCard'
import type { AgentMission, AgentRun, AssistantMessage as AssistantMessageType, Lettability } from '../../api/schema'
import { LettabilityGateCard } from '../../components/occupation/OccupationTab'
import { RecoverableChip, type ListedExpense } from '../../components/expenses/RecoverableChip'
import { ThematicLegend } from '../../components/thematics/ThematicLegend'
import { VoteBar } from '../../components/assemblies/VoteBar'
import { CollapsibleText } from '../../components/assemblies/CollapsibleText'
import { HelpHint } from '../../components/assemblies/HelpHint'
import { StepList, NextAction, type StepDef } from '../../components/assemblies/StepList'
import { useRef, useState } from 'react'
import { CompositePlanLayerPanel } from '../../components/plan/CompositePlanLayerPanel'
import { DEFAULT_LAYERS, type CompositePlanLayers } from '../../components/plan/composite-plan-layers'
import { VisibilityBadge } from '../../components/VisibilityBadge'
import { AssistantLiveTurn, AssistantMessage as AssistantMessageView } from '../../components/assistant/AssistantMessage'
import { Link, createFileRoute, useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AppIcon } from '../../components/ui/icon'
import { mdiAccount, mdiArchiveOutline, mdiBaby, mdiBackburger, mdiCheck, mdiChevronDown, mdiClose, mdiCog, mdiDelete, mdiFitToScreenOutline, mdiFolderMove, mdiForwardburger, mdiHome, mdiMagnify, mdiMagnifyMinusOutline, mdiMagnifyPlusOutline, mdiPencil, mdiLan, mdiRefresh, mdiSourceMerge, mdiSwapHorizontal, mdiTrashCanOutline } from '@mdi/js'

import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { PageHeader } from '../../components/shared/PageHeader'
import { OverflowMenu, OverflowMenuItem, OverflowMenuSeparator } from '../../components/shared/OverflowMenu'
import { SignaturePad, isHandwritingReady, type Handwriting } from '../../components/signatures/SignaturePad'
import { ImpersonationBannerView } from '../../components/impersonation/ImpersonationBanner'
import { SECTION_ICONS } from '../../lib/section-icons'
import { AttentionBadge, AttentionDot } from '../../components/ui/attention-badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip'
import { useGlobalFileDrop } from '../../hooks/use-global-file-drop'
import { FileDropOverlay } from '../../components/shared/FileDropOverlay'
import { NetworkSwatch } from '../../components/plan-editor/NetworkControls'
import { Can, useCan } from '../../contexts/PermissionsContext'
import { AssetTypePicker } from '../../components/asset-type-picker/AssetTypePicker'
import { RelationsPanel } from '../../components/asset-relations/RelationsPanel'
import { DocumentPreviewCard } from '../../components/documents/DocumentPreviewCard'
import { HierarchyTree } from '../../components/hierarchy/HierarchyTree'
import { EquipmentRowTree } from '../../components/equipment/TradeAccordion'
import type { EquipmentRow as EquipmentTabRow } from '../../api/equipment-tab-types'
import type { HierarchyTreeNode } from '../../components/hierarchy/HierarchyTree'
import type { components } from '../../api/schema'
import { BulkActionsBar } from '../../components/BulkActionsBar'
import { Checkbox } from '../../components/ui/checkbox'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { EditableValue } from '../../components/ui/editable-value'
import { confirmMatches } from '../../hooks/use-archive'
import { AddIconButton } from '../../components/ui/add-icon-button'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { Skeleton } from '../../components/ui/skeleton'
import { EmptyState } from '../../components/ui/empty-state'
import { MultilingualInput } from '../../components/ui/multilingual-input'
import { StatusPill, STATUS_PILL_CONFIG, type EvaluationStatus } from '../../components/AssetCompliance'
import { CategoryCards } from '../../components/steering/CategoryCards'
import type { SteeringCategory } from '../../components/steering/steering-categories'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form'
import { assetTypeColorVar } from '../../components/graph/colors'
import { LayerPanel } from '../../components/plan-editor/LayerPanel'
import { defaultLayerStates } from '../../components/plan-editor/engine/render'
import type { SceneDoc } from '../../components/plan-editor/engine/scene'
import { SuggestedTextInput } from '../../components/properties/SuggestedTextInput'
import { DistributionStrip } from '../../components/benchmark/DistributionStrip'
import type { PropertyDefinition } from '../../hooks/useQueries'
import { AssetTypeTile } from '../../components/assets/AssetTypeTile'
import { FieldDiff } from '../../components/ontology/FieldDiff'
import { WordDiff } from '../../components/knowledge/KnowledgeProposalDialog'
import { Badge } from '../../components/ui/badge'
import { LotBreakdown } from '../../components/assets/LotBreakdown'
import { ChecklistStatusPill } from '../../components/ContractChecklistParts'
import { ContractTypeGroupSection } from '../../components/ContractTypeGroupSection'
import { CampaignEntryField } from '../../components/campaigns/CampaignEntryField'
import { RiserDiagramView } from '../../components/plan/RiserDiagram'
import type { RiserDiagram as RiserDiagramPayload } from '../../components/plan/riser-diagram-layout'
import { riserMarkup } from '../../components/plan-editor/engine/render'
import type { CampaignEntry } from '../../hooks/useCampaigns'

export const Route = createFileRoute('/$org/patterns')({
  component: PatternsPage,
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PatternSectionProps {
  id: string
  title: string
  rule: string
  files: string[]
  donts?: string[]
  children: React.ReactNode
}

// One pattern entry on the live reference page. Each section gets a stable
// `id` anchor so docs/frontend/patterns.md can link directly to it and the
// Playwright snapshot test can assert against it.
function PatternSection({ id, title, rule, files, donts, children }: PatternSectionProps) {
  return (
    <section
      id={id}
      data-testid={`pattern-section-${id}`}
      className="scroll-mt-24 border-b border-border py-10 first:pt-0 last:border-b-0"
    >
      <h2 className="mb-2 text-xl font-semibold text-foreground">{title}</h2>
      <p className="mb-6 max-w-3xl text-sm text-muted-foreground">{rule}</p>
      <div className="mb-6 rounded-lg border border-border bg-background p-6">{children}</div>
      <PatternMeta files={files} donts={donts} />
    </section>
  )
}

interface PatternMetaProps {
  files: string[]
  donts?: string[]
}

// Per-section "small print": canonical implementation file(s) and the
// optional "do not" list that surfaces anti-patterns explicitly.
function PatternMeta({ files, donts }: PatternMetaProps) {
  return (
    <div className="space-y-3 text-xs text-muted-foreground">
      <div>
        <span className="font-semibold text-foreground">Canonical files:</span>{' '}
        {files.length === 0 ? (
          <span className="italic">none yet — refactor signal, see Don'ts.</span>
        ) : (
          files.map((f, idx) => (
            <span key={f}>
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{f}</code>
              {idx < files.length - 1 && ', '}
            </span>
          ))
        )}
      </div>
      {donts && donts.length > 0 && (
        <div>
          <span className="font-semibold text-foreground">Don't:</span>
          <ul className="ml-4 mt-1 list-disc space-y-1">
            {donts.map((d, idx) => (
              <li key={idx}>{d}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const SECTIONS = [
  { id: 'pattern-page-header-detail', title: '1. Page header — entity detail' },
  { id: 'pattern-page-header-list', title: '2. Page header — list page' },
  { id: 'pattern-inline-edit-text', title: '3. Inline editing — text' },
  { id: 'pattern-inline-edit-multilingual', title: '4. Inline editing — multilingual' },
  { id: 'pattern-action-buttons', title: '5. Action buttons' },
  { id: 'pattern-permission-gated-controls', title: '5b. Permission-gated controls' },
  { id: 'pattern-confirmation-dialog', title: '6. Confirmation dialog' },
  { id: 'pattern-lists', title: '7. Lists' },
  { id: 'pattern-forms', title: '8. Forms' },
  { id: 'pattern-notifications', title: '9. Notifications' },
  { id: 'pattern-breadcrumbs', title: '10. Breadcrumbs' },
  { id: 'pattern-states', title: '11. Loading / empty / error states' },
  { id: 'pattern-icons', title: '12. Icons' },
  { id: 'pattern-hierarchical-picker', title: '13. Hierarchical + searchable picker' },
  { id: 'pattern-bulk-actions', title: '14. List multi-select & bulk actions' },
  { id: 'pattern-status-pill', title: '15. Status pill' },
  { id: 'pattern-graph-explorer', title: '16. Graph explorer — canvas page' },
  { id: 'pattern-document-page-highlight', title: '17. Document page highlight — source provenance viewer' },
  { id: 'pattern-typed-relations', title: '18. Typed relations — grouped links' },
  { id: 'pattern-document-preview-card', title: '19. Document preview card — collapsible inline preview' },
  { id: 'pattern-hierarchy-tree', title: '20. Hierarchy tree — shared master-detail tree' },
  { id: 'pattern-facet-cards', title: '21. Facet cards — primary axis with server counts' },
  { id: 'pattern-scene-layer-panel', title: '22. Scene layer panel' },
  { id: 'pattern-suggested-text-input', title: '23. Inline editing — free-text combobox with suggestions' },
  { id: 'pattern-audience-badge', title: '24. Audience badge — who else sees this row' },
  { id: 'pattern-comparison-matrix', title: '25. Comparison matrix — capability grid' },
  { id: 'pattern-global-file-drop', title: '26. Global file drop — the page is the target' },
  { id: 'pattern-queue-console', title: '27. Queue console — browsing vs reading' },
  { id: 'pattern-footer-status-row', title: '28. Footer status row — trailing right-aligned element' },
  { id: 'pattern-distribution-strip', title: '29. Distribution strip — where one value stands' },
  { id: 'pattern-display-first-editing', title: '31. Click-to-edit values, pencil for sets' },
  { id: 'pattern-review-diff-then-apply', title: '32. Review a diff, then apply' },
  { id: 'pattern-header-overflow-menu', title: '33. Detail header — one primary action + overflow menu' },
  { id: 'pattern-impersonation-banner', title: '34. Impersonated session banner' },
  { id: 'pattern-signature-pad', title: '35. Handwritten signature — draw or type' },
  { id: 'pattern-colour-by', title: '36. Colour by — a thematic and its legend' },
  { id: 'pattern-typed-delete', title: '37. Irreversible delete — impact preview and typed name' },
  { id: 'pattern-select-all-matching', title: '38. Select all N matching' },
  { id: 'pattern-viewer-layer-panel', title: '45. Viewer layer panel — hide what you are not reading' },
  { id: 'pattern-count-by-nature', title: '46. Count by nature — a total and its linked parts' },
  { id: 'pattern-grouped-list', title: '47. Grouped list by type' },
  { id: 'pattern-survey-field', title: '48. Survey field — confirm or correct a known value' },
  { id: 'pattern-agent-run-card', title: '49. Agent run card' },
  { id: 'pattern-assistant-answer', title: '50. Assistant answer' },
  { id: 'pattern-riser-diagram', title: '51. Risers and the column diagram' },
  { id: 'pattern-lettability-gate', title: '52. Lettability gate card' },
  { id: 'pattern-proposed-value', title: '53. Proposed value — not yet confirmed' },
  { id: 'pattern-secret-once', title: '54. Secret shown once' },
  { id: 'pattern-collapsible-sidebar-group', title: '55. Labelled collapsible sidebar group' },
  { id: 'pattern-nested-components', title: '56. Nested components in a grouped list' },
  { id: 'pattern-attention-badge', title: '57. Attention badge' },
] as const

// ---------------------------------------------------------------------------
// Section content
// ---------------------------------------------------------------------------

function PageHeaderDetailExample() {
  return (
    <div className="space-y-4">
      <PageHeader
        icon={SECTION_ICONS.assets}
        title="15 rue de la Paix"
        titleAside={
          <code className="rounded bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
            BG001 · ext. 0001-PA
          </code>
        }
        actions={
          <>
            <Button
              variant="outline"
              aria-label="Archive"
              title="Archive"
              data-testid="pattern-action-archive"
            >
              <AppIcon path={mdiArchiveOutline} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">Archive</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Edit"
              title="Edit"
              data-testid="pattern-action-edit"
            >
              <AppIcon path={mdiPencil} size="sm" />
            </Button>
            <Button
              variant="destructive"
              size="icon"
              aria-label="Delete"
              title="Delete"
              data-testid="pattern-action-delete"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Identity</p>
          <p className="text-sm text-foreground">15 rue de la Paix · BG001</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Location</p>
          <p className="text-sm text-foreground">75002 Paris, France</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Volumetry</p>
          <p className="text-sm text-foreground">
            1 240 m² <span className="text-muted-foreground">SUB</span>
          </p>
        </div>
      </div>
    </div>
  )
}

function PageHeaderListExample() {
  return (
    <div className="space-y-4">
      <PageHeader
        icon={SECTION_ICONS.assets}
        title="Assets"
        actions={
          <Button data-testid="pattern-list-primary-action">
            <AppIcon path={mdiPencil} size="sm" className="mr-2" />
            New asset
          </Button>
        }
      />
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/30 p-3">
        <div className="relative flex-1 min-w-[180px]">
          <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search…"
            className="pl-9"
            data-testid="pattern-list-search"
          />
        </div>
        <Button variant="outline" size="sm" data-testid="pattern-list-filter">
          Type <AppIcon path={mdiChevronDown} size="sm" tone="muted" className="ml-1" />
        </Button>
        <Button variant="outline" size="sm" data-testid="pattern-list-sort">
          Sort <AppIcon path={mdiChevronDown} size="sm" tone="muted" className="ml-1" />
        </Button>
      </div>
    </div>
  )
}

function InlineEditTextExample() {
  const [value, setValue] = useState('Building A')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const save = () => {
    setValue(draft)
    setEditing(false)
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save()
          if (e.key === 'Escape') cancel()
        }}
        className="max-w-sm"
        data-testid="pattern-inline-edit-input"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value)
        setEditing(true)
      }}
      className="rounded px-2 py-1 text-left text-base font-medium text-foreground hover:bg-muted"
      data-testid="pattern-inline-edit-trigger"
    >
      {value} <span className="text-xs text-muted-foreground">(click to edit)</span>
    </button>
  )
}

function InlineEditMultilingualExample() {
  const [value, setValue] = useState<{ [key: string]: string }>({
    en: 'Heating fuel',
    fr: 'Combustible chauffage',
  })

  return (
    <div className="max-w-md">
      <MultilingualInput
        value={value}
        onChange={setValue}
        placeholder="Property name"
        required
        data-testid="pattern-inline-edit-multilingual-input"
      />
    </div>
  )
}

function ActionButtonsExample() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        aria-label="Archive"
        title="Archive"
        data-testid="pattern-actionbar-archive"
      >
        <AppIcon path={mdiArchiveOutline} size="sm" className="md:mr-1" />
        <span className="hidden md:inline">Archive</span>
      </Button>
      <Button
        variant="outline"
        size="icon"
        aria-label="Edit"
        title="Edit"
        data-testid="pattern-actionbar-edit"
      >
        <AppIcon path={mdiPencil} size="sm" />
      </Button>
      <Button
        variant="destructive"
        size="icon"
        aria-label="Delete"
        title="Delete"
        data-testid="pattern-actionbar-delete"
      >
        <AppIcon path={mdiTrashCanOutline} size="sm" />
      </Button>
      <AddIconButton
        tooltip="Add child"
        onClick={() => {}}
        testId="pattern-actionbar-add"
      />
    </div>
  )
}

// Permission-gated controls (spec 2026-09-04-02). Every write action asks the
// permission context before rendering: `<Can perm="…">` for JSX, `useCan()`
// when the answer feeds a prop or a branch. Read affordances are NEVER gated
// this way — an inline editor the caller may not write renders read-only, so
// the value stays on screen.
function PermissionGatedControlsExample() {
  const canWriteAssets = useCan('assets.write')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Can perm="assets.write">
          <Button size="sm" data-testid="pattern-can-write-action">
            Archive asset
          </Button>
        </Can>
        <Can
          perm="organization.write"
          fallback={
            <span
              className="text-xs text-muted-foreground"
              data-testid="pattern-can-fallback"
            >
              Hidden — needs organization.write
            </span>
          }
        >
          <Button size="sm" variant="outline">
            Edit organization
          </Button>
        </Can>
      </div>
      <div className="rounded-md border border-border p-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Inline editor — read-only instead of hidden
        </p>
        <p className="mt-1 text-sm text-foreground" data-testid="pattern-can-readonly-row">
          Surface: 1 240 m²{' '}
          {canWriteAssets ? (
            <span className="text-xs text-muted-foreground">(click to edit)</span>
          ) : (
            <span className="text-xs text-muted-foreground">(read-only)</span>
          )}
        </p>
      </div>
    </div>
  )
}

function ConfirmationDialogExample() {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" data-testid="pattern-dialog-trigger">
          Delete asset
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. The asset and its descendants will be moved to the
            archive.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="pattern-dialog-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="pattern-dialog-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ListsExample() {
  return (
    <Table data-testid="pattern-lists-table">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Surface</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className="font-medium">15 rue de la Paix</TableCell>
          <TableCell className="text-muted-foreground">Building</TableCell>
          <TableCell>1 240 m²</TableCell>
          <TableCell className="text-right">
            <Button variant="ghost" size="icon" aria-label="Edit row" title="Edit row">
              <AppIcon path={mdiPencil} size="sm" />
            </Button>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="font-medium">23 avenue de l'Opéra</TableCell>
          <TableCell className="text-muted-foreground">Building</TableCell>
          <TableCell>980 m²</TableCell>
          <TableCell className="text-right">
            <Button variant="ghost" size="icon" aria-label="Edit row" title="Edit row">
              <AppIcon path={mdiPencil} size="sm" />
            </Button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

const formSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  name: z.string().min(2, 'At least 2 characters'),
})

function FormsExample() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', name: '' },
    mode: 'onTouched',
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    // Demo only; no API.
    console.log('submitted', values)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="max-w-md space-y-4"
        data-testid="pattern-forms-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Travolta" {...field} data-testid="pattern-forms-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  {...field}
                  data-testid="pattern-forms-email"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
          data-testid="pattern-forms-submit"
        >
          {form.formState.isSubmitting ? 'Submitting…' : 'Submit'}
        </Button>
      </form>
    </Form>
  )
}

function NotificationsExample() {
  return (
    <div className="space-y-3">
      <Alert>
        <AlertTitle>Heads up</AlertTitle>
        <AlertDescription>
          Information notifications use the default Alert variant — no toast.
        </AlertDescription>
      </Alert>
      <Alert>
        <AlertTitle>Saved</AlertTitle>
        <AlertDescription>
          Success messages live next to the affected control, not over the page as a transient
          toast.
        </AlertDescription>
      </Alert>
      <Alert variant="destructive">
        <AlertTitle>Could not save</AlertTitle>
        <AlertDescription>
          Errors render at the form / control level, not as global toasts.
        </AlertDescription>
      </Alert>
    </div>
  )
}

function BreadcrumbsExample() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">
        Look at the top of this page — the breadcrumb is{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">Home › UI Patterns</code>.
        It is wired by <code className="font-mono text-xs">useBreadcrumbs(...)</code> in this
        route's component, the same way every other route in the app does it.
      </p>
      <p className="text-sm text-muted-foreground">
        The breadcrumb is the canonical "back to parent" affordance — entity detail pages must not
        add a <code className="font-mono text-xs">← Back</code> button on top of it. Fatal-error /
        not-found pages where the breadcrumb may not point at a usable parent are the only
        exception.
      </p>
    </div>
  )
}

function StatesExample() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">Loading</p>
          <Skeleton className="mb-2 h-4 w-3/4" />
          <Skeleton className="mb-2 h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">Empty</p>
          <EmptyState
            icon={SECTION_ICONS.assets}
            tone="solid"
            title="No assets yet"
            description="Get started by creating the first one."
            action={<Button size="sm">Create asset</Button>}
            data-testid="patterns-empty-panel"
          />
        </div>
        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">Error</p>
          <Alert variant="destructive">
            <AlertTitle>Could not load</AlertTitle>
            <AlertDescription>The server returned 500. Try again.</AlertDescription>
          </Alert>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">
            Filter-empty (soft tone, role="status")
          </p>
          <EmptyState
            icon={mdiMagnify}
            live
            title="No asset matches this filter"
            description="Clear the filters to see the whole portfolio again."
            action={
              <Button size="sm" variant="outline">
                Clear filters
              </Button>
            }
            data-testid="patterns-empty-filter"
          />
        </div>
        <div className="rounded-md border border-border p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">
            variant="inline" — sm icon, neutral gradient (cells, side panels)
          </p>
          <EmptyState
            variant="inline"
            icon={SECTION_ICONS.documents}
            title="No document linked"
            data-testid="patterns-empty-inline"
          />
        </div>
      </div>
    </div>
  )
}

function IconsExample() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <AppIcon path={mdiHome} size="sm" />
          <code className="text-[10px] text-muted-foreground">size="sm"</code>
        </div>
        <div className="flex flex-col items-center gap-1">
          <AppIcon path={mdiAccount} size="md" />
          <code className="text-[10px] text-muted-foreground">size="md"</code>
        </div>
        <div className="flex flex-col items-center gap-1">
          <AppIcon path={mdiCog} size="xl" />
          <code className="text-[10px] text-muted-foreground">size="xl"</code>
        </div>
        <div className="flex flex-col items-center gap-1">
          <AppIcon path={mdiMagnify} size="sm" />
          <code className="text-[10px] text-muted-foreground">size="sm"</code>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Use <code className="font-mono text-xs">@mdi/js</code> paths through{' '}
        <code className="font-mono text-xs">AppIcon</code>. Sizes are names on a px grid:{' '}
        <code>size="sm"</code> in a header button, <code>size="md"</code> in the sidebar and
        list rows. Always pair an icon-only button with <code>aria-label</code> +{' '}
        <code>title</code>.
      </p>
    </div>
  )
}

// §26 — Global file drop. The real hook and the real overlay, over a demo
// surface: drag any file onto this page and the overlay appears.
function GlobalFileDropExample() {
  const [dropped, setDropped] = useState<string[]>([])
  const { isDragging } = useGlobalFileDrop((files) => setDropped(files.map((f) => f.name)))

  return (
    <div className="space-y-3" data-testid="pattern-global-file-drop-demo">
      {isDragging && <FileDropOverlay label="Drop your files here" testId="pattern-file-drop-overlay" />}
      <p className="text-sm text-muted-foreground">
        Drag any file onto this page — the whole viewport is the target, and the overlay says so.
      </p>
      {dropped.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {dropped.map((name, i) => (
            <li key={`${name}-${i}`} className="rounded-lg border bg-muted/40 px-2 py-1.5 text-xs">
              {name}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Nothing dropped yet.</p>
      )}
    </div>
  )
}

// §27 — Queue console. The two states side by side, driven by the same
// "is a row selected" flag the real console reads off the URL.
function QueueConsoleExample() {
  const [selected, setSelected] = useState<string | null>(null)
  const [queueCollapsed, setQueueCollapsed] = useState(false)
  const rows = [
    { uid: 'r1', sender: 'mme.dupont@example.com', subject: 'Fuite sous l’évier de la cuisine commune' },
    { uid: 'r2', sender: 'voisin@example.com', subject: 'Ascenseur bloqué entre le 2e et le 3e' },
    { uid: 'r3', sender: 'syndic@example.com', subject: 'Convocation AG — pièces jointes' },
  ]
  const reading = selected !== null
  const columns = !reading
    ? 'sm:grid-cols-[9rem_minmax(0,15rem)_minmax(0,1fr)]'
    : queueCollapsed
      ? 'sm:grid-cols-[2.5rem_minmax(0,1fr)]'
      : 'sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]'

  return (
    <div className="space-y-3" data-testid="pattern-queue-console-demo">
      <p className="text-sm text-muted-foreground">
        Browsing is the queue's own screen. Selecting a row is a route change, and reading mode
        folds the rail into the list header, narrows the list to a strip, and hands the width to
        the thread and its context panel.
      </p>
      <div className={`grid h-72 grid-cols-1 divide-x overflow-hidden rounded-lg border bg-card ${columns}`}>
        {!reading && (
          <nav className="hidden flex-col gap-1 p-2 text-sm sm:flex" aria-label="Folders">
            {['Inbox', 'Waiting', 'Closed', 'All'].map((folder, i) => (
              <span
                key={folder}
                className={`rounded-md px-2.5 py-1.5 ${i === 0 ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground'}`}
              >
                {folder}
              </span>
            ))}
          </nav>
        )}

        {reading && queueCollapsed ? (
          <div className="hidden flex-col items-center pt-2 sm:flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-expanded={false}
              aria-label="Show the conversation list"
              title="Show the conversation list"
              onClick={() => setQueueCollapsed(false)}
              data-testid="pattern-queue-console-expand"
            >
              <AppIcon path={mdiForwardburger} size="sm" />
            </Button>
          </div>
        ) : (
          <div className={`flex min-h-0 flex-col ${reading ? 'hidden sm:flex' : 'flex'}`}>
            <div className="space-y-2 border-b p-2">
              {reading && (
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 min-w-0 flex-1 justify-start gap-1.5 px-2 text-xs">
                    <span className="truncate">Inbox</span>
                    <AppIcon path={mdiChevronDown} size="xs" tone="muted" className="ml-auto opacity-60" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    aria-expanded={true}
                    aria-label="Hide the conversation list"
                    title="Hide the conversation list"
                    onClick={() => setQueueCollapsed(true)}
                    data-testid="pattern-queue-console-collapse"
                  >
                    <AppIcon path={mdiBackburger} size="sm" />
                  </Button>
                </div>
              )}
              <Input className="h-8" placeholder="Search a sender or a subject…" readOnly />
            </div>
            <div className="min-h-0 flex-1 divide-y overflow-y-auto">
              {rows.map((row) => (
                <button
                  key={row.uid}
                  type="button"
                  onClick={() => setSelected(row.uid)}
                  className={`block w-full border-l-2 px-2.5 py-2 text-left ${
                    selected === row.uid ? 'border-l-primary bg-primary/10' : 'border-l-transparent hover:bg-muted/50'
                  }`}
                  data-testid={`pattern-queue-console-row-${row.uid}`}
                >
                  <span className="block truncate text-xs font-medium text-foreground">{row.sender}</span>
                  <span className="line-clamp-1 text-xs text-muted-foreground">{row.subject}</span>
                  {!reading && <span className="line-clamp-1 text-xs text-muted-foreground/70">Bonjour, il y a…</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex min-h-0 flex-col">
          {reading ? (
            <>
              <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
                <span className="truncate text-sm font-semibold text-foreground">
                  {rows.find((r) => r.uid === selected)?.subject}
                </span>
                <Button variant="outline" size="sm" onClick={() => setSelected(null)}>
                  Close
                </Button>
              </header>
              <div className="grid min-h-0 flex-1 grid-cols-1 divide-x sm:grid-cols-[minmax(0,1fr)_11rem]">
                <div className="min-h-0 overflow-y-auto p-3 text-sm text-muted-foreground">
                  <p className="max-w-prose">
                    The thread scrolls here, and the reply card stays pinned under it. Message
                    bodies are capped at a reading measure even though the card spans.
                  </p>
                </div>
                <aside className="space-y-2 bg-muted/20 p-3 text-xs text-muted-foreground">
                  {['Contact', 'Assets', 'Tickets', 'Context'].map((s) => (
                    <div key={s} className="flex items-center gap-1">
                      <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
                      <span className="font-semibold uppercase tracking-wide">{s}</span>
                    </div>
                  ))}
                </aside>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              Pick a conversation to read it
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// §28 — Footer status row. A miniature: the real row (AppSidebar's
// SidebarFooter, spec 2026-09-12-03) needs SidebarProvider plus auth/org
// route context to mount, so this reproduces its shape — a primary
// indicator that grows on the left, a secondary meta element pinned right
// via `ml-auto` — without the live component (refactor signal, see Don'ts).
function FooterStatusRowExample() {
  const [blocked, setBlocked] = useState(false)
  const [hasNewVersion, setHasNewVersion] = useState(true)

  return (
    <div className="space-y-3" data-testid="pattern-footer-status-row-demo">
      <div className="max-w-xs rounded-lg border border-sidebar-border bg-sidebar p-1">
        <div className="flex w-full items-center gap-2 px-2 py-1">
          <button
            type="button"
            onClick={() => setBlocked((b) => !b)}
            className="flex items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm text-sidebar-foreground hover:bg-sidebar-accent"
            data-testid="pattern-footer-status-row-primary"
          >
            {blocked ? '⛔ 3 · 5/10' : '✅ 10/10'}
          </button>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <span
              className="text-[10px] text-muted-foreground/40"
              data-testid="pattern-footer-status-row-secondary"
            >
              v0.42.0
            </span>
            {hasNewVersion && (
              <button
                type="button"
                onClick={() => setHasNewVersion(false)}
                title="New version available"
                className="inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors motion-safe:[&:hover_svg]:animate-spin"
                data-testid="pattern-footer-status-row-reload"
              >
                <AppIcon path={mdiRefresh} size="xs" />
              </button>
            )}
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Click the primary indicator to toggle blocked/done; click reload to dismiss the mismatch.
        The secondary element never moves — it stays right-aligned whether or not the primary one
        is even rendered.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PatternsPage() {
  const { org } = useParams({ from: '/$org/patterns' })

  // The page itself must demonstrate the breadcrumb pattern — see §10.
  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'UI Patterns' },
  ])

  return (
    <div>
      <header className="mb-10">
        <PageHeader
          icon={SECTION_ICONS.patterns}
          title="UI Pattern Reference"
          description={
            <span className="block max-w-3xl">
              Live, working examples of every canonical UI pattern in this app — <strong>how we
              compose</strong> the primitives. Open this page when building new UI; match what you
              see here. New patterns must be added in the same PR that introduces them — see{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">
                docs/frontend/patterns.md
              </code>
              .
            </span>
          }
        />
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
          Looking for a <strong>primitive</strong> rather than a composition — a Button variant, a
          Badge, a colour token, the canonical form spacing? That is the sibling page:{' '}
          <Link
            to="/$org/design-reference"
            params={{ org }}
            className="text-primary underline-offset-4 hover:underline"
            data-testid="patterns-design-reference-link"
          >
            /{org}/design-reference
          </Link>
          . The split is deliberate: <strong>design-reference = what exists</strong> (tokens,
          primitives, conventions); <strong>patterns = how we compose it</strong> (production
          components fed static data, one snapshot per section). A composed pattern never moves to
          design-reference; a primitive never gets a patterns section.
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">
        <nav
          aria-label="Patterns table of contents"
          className="mb-8 lg:sticky lg:top-20 lg:mb-0 lg:self-start"
          data-testid="patterns-toc"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            On this page
          </p>
          <ul className="space-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`patterns-toc-${s.id}`}
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <main>
          <PatternSection
            id="pattern-page-header-detail"
            title="1. Page header — entity detail"
            rule="Three semantic slots (Identity / Location / Volumetry); icon-only Edit & Delete; responsive labelled action bar; never a ← Back button. The H1 is click-to-edit for renames; the external-id 'code' chip carries the edit affordance for the external code (hover pencil; '+ external code' CTA when empty). The pinned summary card never duplicates name / external code / asset type — those live on the title row."
            files={['front/src/components/shared/PageHeader.tsx', 'front/src/lib/section-icons.ts', 'front/src/lib/asset-header-slots.ts', 'front/src/components/AssetDetailPage.tsx']}
            donts={[
              'Add a ← Back button next to the title — the breadcrumb is the back affordance.',
              'Use the same prominence for Edit/Delete as for primary actions — they are secondary controls.',
              'Repeat name / external code / asset type in the pinned summary card — they already live on the title row.',
              'Hide rename behind a separate Edit button — clicking the H1 is the canonical rename affordance.',
              'Hand-roll the title row — PageHeader owns the page\'s single heading, its icon square and its type scale.',
            ]}
          >
            <PageHeaderDetailExample />
          </PatternSection>

          <PatternSection
            id="pattern-page-header-list"
            title="2. Page header — list page"
            rule="Title + primary action + filter row; URL-backed state for filter / sort / pagination."
            files={['front/src/components/shared/PageHeader.tsx', 'front/src/lib/section-icons.ts', 'front/src/routes/$org/assets.tsx']}
            donts={[
              'Store filter / sort / pagination state in component state only — every list change should reflect in the URL.',
              'Use a toast to confirm a filter applied — let the list itself reflect the change.',
              'Write your own H1 element — page-headers.test.ts fails the build for it.',
            ]}
          >
            <PageHeaderListExample />
          </PatternSection>

          <PatternSection
            id="pattern-inline-edit-text"
            title="3. Inline editing — text"
            rule="Click-to-edit; Enter saves; Escape cancels; blur saves; PATCH on confirm; refresh through the cache helper."
            files={['front/src/components/PropertyEditPage.tsx (reference)', 'front/CLAUDE.md "Cache Invalidation"']}
            donts={[
              'Open a modal dialog to edit a single text field.',
              'Save on every keystroke — wait for Enter / blur, then PATCH once.',
            ]}
          >
            <InlineEditTextExample />
          </PatternSection>

          <PatternSection
            id="pattern-inline-edit-multilingual"
            title="4. Inline editing — multilingual"
            rule="EN required, FR optional; tab between locales; same Enter / Escape rules as plain text."
            files={['front/src/components/ui/multilingual-input.tsx', 'docs/frontend/internationalization.md']}
            donts={[
              'Render two inputs side-by-side without tabs — locale tabs are the canonical affordance.',
              'Validate FR as required — only EN is required by convention.',
            ]}
          >
            <InlineEditMultilingualExample />
          </PatternSection>

          <PatternSection
            id="pattern-action-buttons"
            title="5. Action buttons"
            rule="Header buttons collapse to icon-only below md; Edit & Delete are always icon-only; pair every icon-only button with aria-label + title."
            files={['front/src/components/ui/button.tsx', 'front/src/components/ui/add-icon-button.tsx']}
            donts={[
              'Use <button className="..."> directly — wrap with the canonical Button component for consistent focus / disabled / hover styling.',
              'Ship an icon-only button without aria-label and title — screen readers and tooltip hovers both depend on them.',
            ]}
          >
            <ActionButtonsExample />
          </PatternSection>

          <PatternSection
            id="pattern-permission-gated-controls"
            title="5b. Permission-gated controls"
            rule={'Every write action asks the permission context before rendering — <Can perm="assets.write"> in JSX, useCan() when the answer feeds a prop; read affordances are never hidden this way: an inline editor the caller cannot write renders read-only so the value stays on screen.'}
            files={[
              'front/src/contexts/PermissionsContext.tsx',
              'front/src/lib/route-permissions.ts',
              'front/src/lib/notifications.ts',
            ]}
            donts={[
              'Gate on a role code (`role === "admin"`) — roles are org data and can be customized; gate on the permission the backend actually checks.',
              'Hide an inline property editor when write is missing — the value disappears with it. Render it read-only instead.',
              'Show a raw 403 error string — the API client raises the standard `permission_denied` notification centrally.',
              'Disable a control instead of hiding it, unless the reason is explained (a role you may not assign shows disabled + why).',
            ]}
          >
            <PermissionGatedControlsExample />
          </PatternSection>

          <PatternSection
            id="pattern-confirmation-dialog"
            title="6. Confirmation dialog"
            rule="Always AlertDialog from @radix-ui/react-alert-dialog; never confirm() / window.confirm()."
            files={['front/src/components/ui/alert-dialog.tsx', 'front/src/components/ui/delete-confirm-dialog.tsx']}
            donts={[
              'Call window.confirm() — it blocks the event loop and breaks browser-automation testing.',
              'Use a Dialog (non-Alert) for destructive confirmations — AlertDialog has the right semantics for "are you sure?".',
            ]}
          >
            <ConfirmationDialogExample />
          </PatternSection>

          <PatternSection
            id="pattern-lists"
            title="7. Lists"
            rule="Sortable / filterable / paginated tables; URL-backed state; consistent empty / loading / error shells."
            files={['front/src/components/ui/table.tsx', 'front/src/routes/$org/assets.tsx']}
            donts={[
              'Reach for a third-party data-grid for simple sortable lists — the Table primitive plus URL state covers most cases.',
              'Use shimmer placeholders that don\'t match the real row layout — the loading skeleton should mirror the eventual rows.',
            ]}
          >
            <ListsExample />
          </PatternSection>

          <PatternSection
            id="pattern-forms"
            title="8. Forms"
            rule="react-hook-form + Zod resolver; field-level error messages; submit button disabled until form is valid."
            files={['front/src/components/ui/form.tsx', 'front/src/components/PropertyEditPage.tsx']}
            donts={[
              'Validate inputs by hand inside onChange — let Zod own the schema.',
              'Submit a form that has known invalid values — the submit button must reflect form.formState.isValid.',
            ]}
          >
            <FormsExample />
          </PatternSection>

          <PatternSection
            id="pattern-notifications"
            title="9. Notifications"
            rule="Non-blocking inline notifications (Alert component); never a toast for success."
            files={['front/src/components/ui/alert.tsx']}
            donts={[
              'Pull in a toast library — toasts are explicitly disallowed in this app.',
              'Replace the affected control\'s validation message with a global notification — keep error messages near the field that triggered them.',
            ]}
          >
            <NotificationsExample />
          </PatternSection>

          <PatternSection
            id="pattern-breadcrumbs"
            title="10. Breadcrumbs"
            rule="Always present; truncation behaviour; no ← Back button anywhere (the breadcrumb is the affordance)."
            files={['front/src/hooks/useBreadcrumbs.ts', 'front/src/contexts/BreadcrumbContext.tsx']}
            donts={[
              'Add a ← Back button next to the page title — the breadcrumb already provides that affordance.',
              'Forget to wire useBreadcrumbs in a new route — the breadcrumb context resets on each route mount.',
            ]}
          >
            <BreadcrumbsExample />
          </PatternSection>

          <PatternSection
            id="pattern-states"
            title="11. Loading / empty / error states"
            rule="Three canonical shells, used everywhere — a shimmering Skeleton, the EmptyState primitive, an Alert. EmptyState is the only empty state: panel or inline, soft glyph for a filter miss and solid for a first run, never a hand-rolled dashed box."
            files={[
              'front/src/components/ui/empty-state.tsx',
              'front/src/components/ui/skeleton.tsx',
              'front/src/components/ui/alert.tsx',
            ]}
            donts={[
              'Show a centred spinner with no layout context — use a Skeleton that matches the eventual content shape.',
              'Render an empty state without a primary action — empty is a launch point, not just a message.',
              'Hand-roll an empty state out of a dashed box and a grey sentence — a dashed border is a DROP TARGET, not an absence. Render EmptyState.',
            ]}
          >
            <StatesExample />
          </PatternSection>

          <PatternSection
            id="pattern-icons"
            title="12. Icons"
            rule="MDI via @mdi/js, rendered through AppIcon (a named size scale, never a raw rem multiplier); icon-only buttons require aria-label + title. An asset or an asset type is drawn by AssetTypeTile — a gradient tile whose glyph is the type's icon and whose colour is its family."
            files={['docs/frontend/icons.md', 'front/src/components/ui/icon.tsx', 'front/src/components/assets/AssetTypeTile.tsx']}
            donts={[
              'Use lucide / heroicons / emoji for entity icons — see docs/frontend/icons.md for the canonical mapping.',
              'Draw an asset type with a bare IconTile, or resolve its glyph at the call site — render AssetTypeTile.',
              'Forget aria-label / title on icon-only buttons.',
            ]}
          >
            <IconsExample />
          </PatternSection>

          <PatternSection
            id="pattern-hierarchical-picker"
            title="13. Hierarchical + searchable picker"
            rule="One trigger button shows the current selection and its breadcrumb. The popover combines drill-down (chevron on each row) with a search input — search is current-level first, with automatic fallback to a global search across the whole tree if nothing matches at the current level."
            files={[
              'front/src/components/asset-type-picker/AssetTypePicker.tsx',
              'front/src/components/asset-type-picker/asset-type-picker.ts',
            ]}
            donts={[
              'Render multiple cascading <select> dropdowns (one per depth) — they fragment focus and hide search.',
              'Make the user clear the search to drill down — search and drill-down belong in one surface.',
            ]}
          >
            <HierarchicalPickerExample org={org} />
          </PatternSection>

          <PatternSection
            id="pattern-bulk-actions"
            title="14. List multi-select & bulk actions"
            rule="A leading checkbox column (tri-state header = select-all-in-current-filter, shift-click for range) highlights selected rows; selection is ephemeral component state that clears on any filter/search change. When ≥1 row is selected a sticky BulkActionsBar appears between the filters and the table with a {n} selected counter + clear (X) and the batch actions, each confirming via AlertDialog (destructive) or a small picker Dialog, calling one capped bulk endpoint and reporting success/partial via a non-blocking notification."
            files={[
              'front/src/components/BulkActionsBar.tsx',
              'front/src/components/DocumentsPage.tsx',
              'front/src/hooks/useQueries.ts',
            ]}
            donts={[
              'Persist selection in the URL or across filter changes — hidden selected rows make bulk delete dangerous.',
              'Confirm destructive bulk actions with window.confirm() or report results with a toast.',
              'Fan out N per-row requests from the client — call the single capped bulk endpoint instead.',
            ]}
          >
            <BulkActionsExample />
          </PatternSection>

          <PatternSection
            id="pattern-status-pill"
            title="15. Status pill"
            rule="One shared status-pill config map (never scattered ternaries) drives the tone + MDI icon + i18n label for every evaluation status, reused identically by rows, chips, and counters. Colors are theme tokens only (success/warning/destructive/muted), never hardcoded Tailwind palette classes. Ordering (config.order) doubles as the actionable-first sort key: doubt (NON_COMPLIANT/EXPIRING/ERROR/UNKNOWN) surfaces before reassurance (COMPLIANT), with NOT_APPLICABLE always last and visually muted."
            files={[
              'front/src/components/AssetCompliance.tsx',
            ]}
            donts={[
              'Scatter per-status ternaries across rows/chips/headers — one config map is the single source of truth.',
              'Hardcode Tailwind palette classes (text-green-600, bg-red-50) — use the success/warning/destructive/muted theme tokens so dark mode stays correct.',
              'Let NOT_APPLICABLE compete visually with actionable statuses — it must read as quiet/muted, last in every list.',
            ]}
          >
            <StatusPillExample />
          </PatternSection>

          <PatternSection
            id="pattern-graph-explorer"
            title="16. Graph explorer — canvas page"
            rule="A full-height canvas page (React Flow + d3-force) that explores an entity's relationships by expand-on-demand: a focal node seeds the graph and the user pulls in neighbors one hop at a time, keeping the on-screen node count bounded. Nodes are real DOM cards (an AssetTypeTile for the type, name, ~3 property rows, optional picture, type-hashed accent border from the --chart-N theme tokens — never hardcoded colors); the focal node is highlighted. Hierarchy edges are plain/dashed; typed relations are directional and labelled. Every node affordance (expand / collapse / open) carries a data-testid, and view state (focus + expanded set) lives in the URL (?focus=&expanded=) so the graph is bookmarkable and replayable. All API→graph normalization goes through a single adapter module."
            files={[
              'front/src/routes/$org/graph.tsx',
              'front/src/components/graph/AssetNode.tsx',
              'front/src/components/graph/adapter.ts',
              'front/src/components/graph/forceLayout.ts',
              'front/src/components/graph/colors.ts',
            ]}
            donts={[
              'Render the whole portfolio at once — cap on-screen nodes via expand-on-demand and a soft cap (~200) that prompts to collapse first.',
              'Hardcode node colors — hash the asset_type_code into the --chart-N theme tokens so it holds in light and dark.',
              'Touch response envelopes outside the adapter — it is the single normalization point, so a future backend endpoint changes only that module.',
              'Use a toast for anything — the page follows the no-toast rule like the rest of the app.',
            ]}
          >
            <GraphExplorerExample />
          </PatternSection>

          <PatternSection
            id="pattern-document-page-highlight"
            title="17. Document page highlight — source provenance viewer"
            rule="A page image (from the document-page render endpoint) with a read-only SVG overlay (viewBox='0 0 1 1', normalized [0,1] coordinates used directly as SVG units) drawing one frame per bounding box — fill/stroke on var(--primary) only, never hardcoded. The stroke is expressed in SCREEN pixels via vector-effect='non-scaling-stroke' (2 px), never in page units: under preserveAspectRatio='none' a page-unit stroke scales differently on each axis and swallows a word-sized box whole. The frame is drawn AROUND the box — inflated by a fixed ~4 screen px derived from a ResizeObserver on the stage — and the interior is left untouched; the rest of the page is dimmed instead, with one var(--background) rect at fillOpacity 0.35 masked by the inflated boxes, so the highlighted words keep full contrast. The view OPENS FRAMED on the boxes: a pan/zoom stage (wheel around the cursor, drag-to-pan, +/−/Fit page) animates from fit-to-page to the zoom at which the union of the boxes reads on screen, and ends there — zoom is view state, never URL state (only page and hl are). Attention is a ONE-SHOT pulse (a halo that fades out 2-3 times over ~1.2 s, gated by prefers-reduced-motion), never a loop. PropertySourceBadge's popover shows the verbatim quote plus a 'view in document' deep link (?page=N&hl=x,y,w,h;…) whenever source_details.page is set, following a 3-rung degradation ladder: geometry_status 'resolved' → boxed highlight, anything else with a page → page image with no boxes (and no framing), no page at all → today's plain document link. The document detail page renders this as a mode (?page=), not a separate route, so the breadcrumb and no-Back-button rule hold."
            files={[
              'front/src/components/documents/DocumentPageHighlight.tsx',
              'front/src/components/PropertySourceBadge.tsx',
              'front/src/lib/documentPageHighlight.ts',
            ]}
            donts={[
              'Hardcode highlight colors — fill/stroke must come from var(--primary) so the overlay holds in light and dark.',
              'Express the stroke in page units — a viewBox-unit stroke under preserveAspectRatio="none" scales per axis and covers the very word it points at; use vector-effect="non-scaling-stroke" and a screen-pixel width.',
              'Fill the box — the frame goes around it (inflated by screen px) and the page is dimmed through a mask, so the highlighted content stays at full contrast.',
              'Put a CSS transform on a wrapper holding the overlay — non-scaling-stroke only neutralizes transforms inside the SVG, so the "2 px" stroke would render at 2 × zoom px; transform the image and carry the view in the overlay\'s coordinates.',
              'Leave a word-sized box at fit-to-page — open framed on the boxes; but do not zoom a highlight that is already legible (a full text line at fit-to-page stays put).',
              'Push zoom/pan into the URL — page and hl are the only search state; zoom is view state, same rule as the plan viewer.',
              'Blink the box forever — one short pulse, then still; a permanently flashing element is an accessibility and attention-fatigue problem.',
              'Add drag handles, resize, or hand-editing to this viewer — it is read-only; PlanCanvas is the editable annotation surface, a different pattern.',
              'Open a new route for the page view — extend the existing document detail page with a ?page= mode so the breadcrumb and no-Back-button rule are not duplicated.',
              'Skip the degradation ladder — a page-only quote (no boxes yet) must still render the page image, never an error state.',
            ]}
          >
            <DocumentPageHighlightExample />
          </PatternSection>

          <PatternSection
            id="pattern-typed-relations"
            title="18. Typed relations — grouped links, managed inline"
            rule="Group an entity's typed relations by semantic layer, driven by catalog metadata — never by hardcoded type codes — so a custom type an org adds later lands in the right place automatically. Two top-level sections (i18n EN+FR): Composition (relation types with aggregating: true — 'counts as part of' links that feed rollups) and Functional links (everything else — pure arrows). One collapsible group per relation type using the catalog's emoji / color / ordering / localized name, and one labeled list per direction inside it. A direction heading is '<type name> · <counterpart role>' — it names what the rows listed under it ARE (Dessert · desservi), the arrow carries the direction, and it is article-free: role labels are free-form localized catalog strings, so the frontend can never know their gender or initial phoneme and must never prefix an article. What can be created comes from GET /asset-types/{uid}/possible-relation-types — no frontend list of asset-type codes — and each row carries an icon-only delete confirmed with the shared AlertDialog. Wherever the same entities are browsed (tree rows, lists, graph nodes), the relation count from ?include=link_counts renders as a link-icon badge so a link is visible without opening the entity."
            files={[
              'front/src/lib/relation-grouping.ts',
              'front/src/components/asset-relations/RelationsPanel.tsx',
              'front/src/components/asset-relations/AddRelationDialog.tsx',
              'front/src/components/asset-relations/AssetRelationsBadge.tsx',
            ]}
            donts={[
              'Bucket relation types by hardcoded code (e.g. "group_member") — bucket by the catalog\'s aggregating flag so custom org-defined types sort correctly without a frontend change.',
              'Re-derive role/direction wording locally — use the catalog\'s source_role / target_role (localized jsonb), never a per-type i18n label map.',
              'Put an article in front of a catalog role ("le {role}") — the string is free-form and localized, so no hardcoded article can be right for every value; keep the heading article-free.',
              'Show a raw type/enum code in a row badge — resolve it against the org catalogue and fall back to the code only when it is unknown.',
              'Repeat the structural parent (parent_asset_uid) inside a relations panel — the breadcrumb already owns it.',
              'Gate the add action behind a hardcoded map of entity type codes — ask the API which relation types the type can take part in, and fall back to a read-only panel when it answers nothing.',
              'Pre-hide options to avoid a cardinality conflict — the backend is the authority; surface its error message in the dialog instead.',
            ]}
          >
            <TypedRelationsExample org={org} />
          </PatternSection>

          <PatternSection
            id="pattern-document-preview-card"
            title="19. Document preview card — collapsible inline preview"
            rule="Embed a document's content (image / PDF / video / unsupported) directly on the host entity page instead of link-only rows: a collapsible header (file-type icon, name, chevron, an explicit 'open document' link — clicking the name only toggles, it never navigates) with the mime-category preview lazily mounted the first time the panel opens, so a page with several attachments doesn't fetch every blob on load. One shared DocumentPreview implementation backs both the standalone document detail page and every embedded card. Hosts own the trailing action: an optional onDetach icon button that the host decides the meaning of (direct mutation, or opening its own confirmation dialog) — the card itself never detaches anything."
            files={[
              'front/src/components/documents/DocumentPreviewCard.tsx',
              'front/src/components/documents/DocumentPreview.tsx',
              'front/src/components/interventions/InterventionDocumentsSection.tsx',
              'front/src/components/ExpensePage.tsx',
              'front/src/components/ContractDocuments.tsx',
            ]}
            donts={[
              'Re-implement the mime-category switch (image/pdf/video/unsupported) locally — DocumentPreview is the one implementation; the document detail page uses it too.',
              'Mount the iframe/img/video before the panel has been opened once — several attached documents on one page must not fetch every blob eagerly.',
              'Detach directly from the card — pass onDetach and let the host decide (a plain mutation, or its own AlertDialog confirmation).',
              "Make the document name's click target also navigate — toggling and navigating are different affordances (chevron/name toggles, a separate icon link navigates).",
            ]}
          >
            <DocumentPreviewCardExample org={org} />
          </PatternSection>

          <PatternSection
            id="pattern-hierarchy-tree"
            title="20. Hierarchy tree — shared master-detail tree"
            rule="Every type system that is a tree (folder-layout folders, document types, asset types) browses and restructures itself through one shared HierarchyTree, never a bespoke per-page copy. The tree is read-only navigation by default — selection is reflected in the URL so a detail panel on the right is bookmarkable — and drag & drop lives behind an explicit edit-mode toggle in the tree header. Dropping onto a node re-parents; dropping into the thin gap between siblings reorders, and those gaps only exist when the consumer opted into ordering (orderable). Ordering is optional per consumer: folder layouts persist `ordering`, document types render alphabetically and only re-parent. The tree never writes anything: it emits onMove({ uid, parentUid, ordering?, siblingOrder }) and the consumer maps it to its own PATCH endpoints. Dropping a node on itself or inside its own subtree is refused by the component. Row glyphs default to a file for a leaf and a folder for a parent (open when expanded); a consumer whose nodes are all one kind of thing overrides them once with icons={{ leaf, collapsed, expanded }} — the folder-layout editor does exactly that, because none of its nodes is a file — while a per-node `icon` keeps winning over both. Labels arrive already localized: the consumer resolves the entity's {en, fr} name with getLocalizedName(name, locale) and lists the locale in its memo dependencies so switching language re-labels the tree live; the tree itself never calls t(). The read-only sibling DocumentFolderTree (asset documents tab + global documents table) is not a second tree implementation: it renders a folder layout resolved for one asset and follows the same idioms — nodes collapsed by default with a chevron toggle carrying aria-expanded, the count badge readable while collapsed, and the collapse state owned by the shared component so a consumer overriding renderFolder (the <tr> rows of the documents table) still participates through expanded / onToggle. Moving a document between its folders is a document-type change and says so: folder pick → type pick when the target maps several types → an AlertDialog naming the current → target type change → the existing document PATCH."
            files={[
              'front/src/components/hierarchy/HierarchyTree.tsx',
              'front/src/components/hierarchy/tree-icons.ts',
              'front/src/routes/$org/folder-layouts/$layoutUid.tsx',
              'front/src/components/folder-layouts/FolderDefinitionEditor.tsx',
              'front/src/components/DocumentTypesTree.tsx',
              'front/src/components/documents/DocumentFolderTree.tsx',
              'front/src/components/documents/DocumentMoveFolderDialog.tsx',
              'front/src/routes/$org/contract-types/index.tsx',
              'front/src/components/contract-types/ContractTypeEditor.tsx',
            ]}
            donts={[
              'Add a fourth bespoke tree component — map your entity onto HierarchyTreeNode instead.',
              'Leave drag & drop always-on: restructuring must be an explicit mode, or plain browsing turns into accidental moves.',
              'Persist an ordering for a consumer that renders alphabetically — pass orderable only when the entity really has a sort column.',
              'Write to the API from inside the tree — it emits onMove and nothing else.',
              'Keep the selection in component state: it belongs in the URL search params so the master-detail view is bookmarkable.',
              'Set the same node.icon on every node to change a tree’s look — that is what the tree-level icons prop is for.',
              'Feed the tree a raw multilingual name map or an English-only label: resolve it against the active locale before it becomes node.label.',
              'Keep the collapse state of DocumentFolderTree in the default folder header: a consumer passing renderFolder would lose it — it belongs in the shared component, surfaced through expanded / onToggle.',
              'Move a document between folders silently: folders are bound to document types, so the move rewrites the type and must be confirmed as such.',
            ]}
          >
            <HierarchyTreeExample />
          </PatternSection>

          <PatternSection
            id="pattern-facet-cards"
            title="21. Facet cards — primary axis with server counts"
            rule="When a list's primary axis has a small, fixed set of values that the user thinks in (three to four, no more), it is a row of cards above the filter row — not another dropdown among the dropdowns. Each card carries its own count, and that count is a server-side aggregate over the whole result set (GET …/stats?group_by=…), never a length taken from the page of rows currently held — a client-side count silently under-reports as soon as the list paginates. A secondary line calls out the actionable slice (high priority, overdue) and is hidden when it is zero rather than rendered as “0”. The active card is the one control that says which axis value is on screen: aria-pressed plus a primary ring, mirrored into the URL so the view is bookmarkable. Values that exist but are not part of the axis (an oversized housekeeping bucket, an archive) stay reachable through a muted footer strip — never promoted to a card that would out-shout the real ones by sheer volume. Its treatment is the token system's: each card is a Card, headed by the family's gradient IconTile, with its figure at text-3xl and a single hover lift — the one place on the page where a big number and a lift are allowed. The gradient marks the family's identity; the count, the hint and the urgent line stay flat."
            files={[
              'front/src/components/steering/CategoryCards.tsx',
              'front/src/components/steering/steering-categories.ts',
              'front/src/components/steering/SteeringPage.tsx',
            ]}
            donts={[
              'Count the rows you fetched — the number must come from a server-side group_by aggregate, or it lies the moment the list paginates.',
              'Turn the axis into a fifth <Select> next to status and sort: the point of the cards is that the axis is not one filter among many.',
              'Add a card per enum value — beyond four the row stops reading as an axis and becomes a chart nobody scans.',
              'Give a high-volume housekeeping bucket its own card: it wins on size and loses the user the three that matter.',
              'Render “0 urgent” — an empty actionable slice is silence, not a zero.',
              'Keep the active value in component state: it belongs in the URL search params.',
              'Re-derive the mapping from a row to its axis value in the browser — the server owns it, the client only paints it.',
            ]}
          >
            <FacetCardsExample />
          </PatternSection>

          <PatternSection
            id="pattern-scene-layer-panel"
            title="22. Scene layer panel — a canvas's contents, grouped and toggled"
            rule="A canvas whose contents come from several owning entities (the plan editor's per-asset geometry, and any future multi-source canvas) lists them in a left panel that is two things at once: one row per semantic layer with a visibility eye and an opacity slider, and, nested under each, one row per element naming its owning entity and its type. Layer rows are the drawing's structure, element rows are its inventory — the panel is where you read what the canvas actually contains. Selection is bidirectional: clicking a row selects on the canvas, and selecting on the canvas scrolls its row into view and highlights it. Elements that will only exist after a save carry a `new` badge, so an explicit-save canvas never hides what is still pending. Visibility/opacity are ephemeral view state and never reach the URL or the API — they are how you look at the drawing, not what it is. When the sources are individually *placed*, a third kind of row lists one entry per placement, selectable as a whole so the canvas can offer move/rotate handles on its bounding box, with a shortcut into the precise-placement mode. The panel renders from a plain document object, so it must never key a memo on a mutable array's identity: an imperative engine mutates in place and the panel takes an explicit `version` counter instead."
            files={[
              'front/src/components/plan-editor/LayerPanel.tsx',
              'front/src/components/plan-editor/PlanEditorPage.tsx',
              'front/src/components/plan-editor/engine/render.ts',
            ]}
            donts={[
              'Put visibility or opacity in the URL: they are a way of looking, not a state worth sharing.',
              'Give the element rows their own selection model — the canvas owns the selection and the panel mirrors it.',
              'Hide elements that have no entity yet: an unsaved element must be visible and badged, never silently absent.',
              'Key the row list on `doc.elements` when an imperative engine mutates that array in place — pass a version counter.',
              'Duplicate the layer list as a second row of chips or a toolbar dropdown: one panel, one place to look.',
            ]}
          >
            <SceneLayerPanelExample />
          </PatternSection>

          <PatternSection
            id="pattern-suggested-text-input"
            title="23. Inline editing — free-text combobox with suggestions"
            rule="A text property whose values are open but repetitive (brand, model) keeps the plain input of §3 and adds a list of the values already recorded on comparable assets, most-used first, with the count next to each. It is opt-in per property definition (`properties.suggestions`), never a default: serial numbers, names and free comments stay bare inputs. The input IS the value — there is no “create « xyz »” row, only a muted hint saying Enter keeps what was typed — so free text stays the normal case and a suggestion is a shortcut towards the dominant spelling. Arrow keys move the highlight, Enter on a highlighted row fills without submitting (a second Enter saves), Escape closes the list before it reaches the parent's cancel. The lookup is non-blocking: loading and empty results show nothing, and a failed request silently leaves a working text input behind."
            files={[
              'front/src/components/properties/SuggestedTextInput.tsx',
              'front/src/components/properties/PropertyValueCell.tsx (PropertyValueEditInput)',
              'docs/database/properties.md#text',
            ]}
            donts={[
              'Turn the suggestions into a closed list — the property is free text and stays free text.',
              'Add a “create « xyz »” row: that would frame free text as the special case instead of the normal one.',
              'Show a popover while loading or when there is nothing to suggest — a property can declare suggestions long before any asset carries a value.',
              'Surface a lookup failure as a notification: the input must keep working as a plain input.',
              'Enable suggestions on every text property by default — an identifier has nothing to suggest.',
            ]}
          >
            <SuggestedTextInputExample />
          </PatternSection>

          <PatternSection
            id="pattern-audience-badge"
            title="24. Audience badge — who else sees this row"
            rule="A resource whose audience the organization controls (documents, interventions, issues) carries a small badge next to its title saying Internal or Shared. The badge is always visible — knowing whether a document is shared with the residents is part of understanding it — and only the toggle is permission-gated: it renders as a button when the caller holds `<resource>.write` ON THAT ROW, as a flat badge otherwise. `useCan(perm, entity)` is what asks: the API stamps each row with the permissions the caller has on it, computed from its scope columns, so the check costs no request. The value shown is the effective one the API resolved (a document with no override inherits its type's `shared_by_default` flag), never re-derived here. Audience is a property of the resource, not of the role — roles never filter by document type, and a manager decides one row at a time."
            files={[
              'front/src/components/VisibilityBadge.tsx',
              'front/src/components/AssetAccessPanel.tsx',
              'front/src/contexts/PermissionsContext.tsx (useCan(perm, entity))',
            ]}
            donts={[
              'Hide the badge from a caller who cannot change it — the audience is information, not an action.',
              'Re-derive the audience in the frontend from the document type: the API already resolved it into `effective_visibility`.',
              'Filter a list by role instead of by audience — a role must never mean “this document type”.',
              'Let a resident flip their own ticket to shared: the toggle needs write on that row, which they do not have.',
            ]}
          >
            <div className="flex flex-wrap items-center gap-3">
              <VisibilityBadge visibility="internal" testId="pattern-visibility-internal" />
              <VisibilityBadge visibility="shared" testId="pattern-visibility-shared" />
            </div>
          </PatternSection>

          <PatternSection
            id="pattern-comparison-matrix"
            title="25. Comparison matrix — capability grid"
            rule="A read-only grid that answers “what can each of these do?” in one screen: rows are the things (resources, features), columns are the actors (roles, plans, environments), and a cell is a compact level token (— / R / RW) plus one small badge per partial grant, with the exact underlying codes on `title` hover. The summary is what is painted; the detail is one hover away; nothing is hidden. What the columns are and in what ORDER is the API's answer — the frontend renders the list it received and never re-sorts. Which cells can even carry a badge comes from the server catalogue too (GET …/permissions), so a narrowing added server-side shows up without a frontend change. Column headers link to their actor's detail, so the matrix doubles as navigation into the editor. The table scrolls inside its own overflow-x-auto container with a sticky first column — the page body never scrolls horizontally — and a short legend under it spells out the tokens."
            files={[
              'front/src/routes/$org/settings/roles/index.tsx (RoleComparisonMatrix, MatrixCell)',
              'front/src/api/roles.ts (useQualifiedActionsByResource)',
              'docs/apis/015_roles.md',
            ]}
            donts={[
              'Make the cells editable — editing belongs to the per-actor editor below the matrix, not to the comparison.',
              'Re-sort the columns client-side: the API already decided the order (built-ins strongest first, then custom by code).',
              'Hard-code which rows carry partial grants — that list is the server catalogue, and a frontend copy is exactly the drift this pattern exists to end.',
              'Spell the full permission codes into every cell: the grid stops being readable, which is why the codes live on hover.',
              'Let the table widen the page — it scrolls in its own container, never the body.',
            ]}
          >
            <ComparisonMatrixExample />
          </PatternSection>

          <PatternSection
            id="pattern-global-file-drop"
            title="26. Global file drop — the page is the target"
            rule="On a page whose whole purpose is one collection of files (a conversation thread, a document tab), dragging a file anywhere on the page attaches it — the listener is on `window` via `useGlobalFileDrop`, not on a bordered rectangle the user has to find, and a full-viewport `FileDropOverlay` says so while the drag is in flight. Two details are load-bearing and are why this is a hook rather than four inline handlers: the open state is a DEPTH COUNTER (`dragleave` fires on every child boundary, so a boolean flickers off mid-page), and only drags whose `dataTransfer.types` include `Files` count (dragging a text selection or a link must not raise the overlay). `preventDefault` on `dragover` is what stops the browser from navigating to the dropped file and losing the page. The overlay is `pointer-events-none` — it is feedback, never the drop target. The picker button stays: a drop zone is a shortcut for people with a mouse, and it is never the only way in."
            files={[
              'front/src/hooks/use-global-file-drop.ts',
              'front/src/components/shared/FileDropOverlay.tsx',
              'front/src/components/conversations/ResidentConversationPage.tsx',
            ]}
            donts={[
              'Track the drag with a boolean — nested elements make dragleave fire constantly and the overlay strobes.',
              'Raise the overlay for any drag: a dragged text selection carries no Files type and is not an upload.',
              'Give the overlay pointer events — it would swallow the drop it exists to announce.',
              'Replace the attach button with the drop zone: touch has no drag, and a page with no picker is unusable on a phone.',
              'Re-implement it per page. AssetDocuments.tsx has a pre-hook zone-scoped copy; new surfaces use the hook.',
            ]}
          >
            <GlobalFileDropExample />
          </PatternSection>

          <PatternSection
            id="pattern-queue-console"
            title="27. Queue console — browsing vs reading"
            rule="A console that is a queue on the left and one open item on the right has TWO states, and one layout serves both badly. Browsing (nothing selected) is the queue's own screen: the folders rail, the wide list, the three-line row. Reading (an item selected) hands the room to what is being read — the rail folds into the list header as one chip, the list narrows to a strip showing avatar + sender + subject only, and the detail side (thread + context panel) takes the rest, with the client pinned to the viewport height so the item's own scroll region is the only one on screen. Which state you are in is URL state (`?selected=`), so a shared link opens the same view; the extra collapse handle that hides the strip entirely is per-viewer convenience and lives in `localStorage`, never in the URL. The toggle carries `aria-expanded` + `aria-label` + `title`, and the collapsed strip leaves a rail behind so it can be brought back. Below the breakpoint where both columns fit, nothing changes: the detail already replaces the list there."
            files={[
              'front/src/components/conversations/ConversationsPage.tsx',
              'front/src/components/conversations/ConversationPanel.tsx',
            ]}
            donts={[
              'Put the collapse state in the URL — a shared link would carry one viewer’s panel preference into everyone else’s session.',
              'Keep the browsing row in the reading strip: at 16rem the date, the channel icon and the preview line are noise the open thread already answers.',
              'Cap the panes with `calc(100vh - <magic number>)`. Pin the client once with a full-height flex column and let the one scrolling region be the item itself.',
              'Re-implement the folder rail for the popover — the same component renders in both placements, so a folder added later appears in both at once.',
              'Refactor signal: this example is a miniature, because ConversationsPage reads its state from the route and cannot be mounted with static data. A queue console extracted from its route would be reusable here as-is.',
            ]}
          >
            <QueueConsoleExample />
          </PatternSection>

          <PatternSection
            id="pattern-footer-status-row"
            title="28. Footer status row — trailing right-aligned element"
            rule="A footer row that pairs a PRIMARY indicator (grows to fill the row, may render nothing) with a SECONDARY meta element (fixed content, pinned right via `ml-auto` on its own wrapper, never `justify-between` — with a single flex child that resolves to flex-start, not flex-end). The secondary element must not inherit the primary's visibility gates: gate each independently on why it needs the gate, not on `it's part of the same row`. It must also not become the primary's child — nesting an interactive element (a reload button) inside another interactive element (a link/button) is invalid HTML and doubles up the click target. The row renders only when at least one side has something to show; each side renders (or doesn't) independently within it."
            files={[
              'front/src/components/AppSidebar.tsx (JobsStatusIndicator, FooterVersion, SidebarFooter)',
            ]}
            donts={[
              'Gate the secondary element on the primary\'s permission/membership/org checks — those exist for the primary\'s own reasons (a live subscription, a poll) and rarely apply to the secondary.',
              'Use `justify-content: space-between` to push a lone secondary element right — with only one flex child it resolves to flex-start, not flex-end.',
              'Nest the secondary\'s interactive control inside the primary\'s `<Link>`/`<button>` — invalid HTML, and a click on one fires both.',
              'Refactor signal: this example is a miniature. AppSidebar\'s real row needs SidebarProvider plus auth/org route context to mount, so it reproduces the shape rather than the live component.',
            ]}
          >
            <FooterStatusRowExample />
          </PatternSection>

          <PatternSection
            id="pattern-distribution-strip"
            title="29. Distribution strip — where one value stands"
            rule="To show where one value sits among its peers, draw the empirical distribution on one line: a dot per peer (so `n` can be counted), a p10–p90 band, a median tick, the alert fence dashed, and the subject on top in the primary colour — amber when it crosses the fence. Never a fitted bell curve: real costs are right-skewed and cohorts small, so a smooth curve lies about the very tail it is meant to show. The reading sits to the right in words — a decile from ten values, a position below that (`4th of 7`), `cohort too small` below the floor with no band at all. Coordinates are percentages so the dots stay round at every width; the strip is plain SVG, because a page carries a dozen of them and a chart library would mount a canvas per line."
            files={['front/src/components/benchmark/DistributionStrip.tsx', 'front/src/components/benchmark/AssetBenchmarkView.tsx']}
            donts={[
              'Fit a normal curve or print a z-score: with eight values no single point can exceed mean + 3σ, so a σ threshold never flags the outlier it exists for.',
              'Show a decile computed from five values — say the position instead.',
              'Hide `n` in a tooltip: a strip without its `n` is a rumour.',
              'Use `preserveAspectRatio="none"` on a viewBox — the dots turn into ellipses as the width changes.',
            ]}
          >
            <DistributionStripExample />
          </PatternSection>

          <PatternSection
            id="pattern-display-first-editing"
            title="31. Click-to-edit values, pencil for sets"
            rule="Edit a single value by clicking it. At rest the value looks like text (no boxed input or select) with a hover background and a pointer cursor; one click opens the editor, Enter and blur save, Escape cancels, a select saves on pick, a textarea saves on Ctrl/Cmd+Enter or blur. No separate pencil for a single value. The one exception is a card that edits a SET of linked entities (tags, stakeholders, assets): it keeps one card-level pencil that becomes a Done button, with its add row open by default and a remove cross on every row; each add/remove commits at once, and Done/Escape return to display mode. Contract detail follows this. Superseded the pencil-gated form of spec 2026-09-19-01 (spec 2026-10-11-29)."
            files={['front/src/components/ContractPage.tsx (canonical)', 'front/src/components/ui/editable-value.tsx', 'front/src/components/ui/NullableDateField.tsx', 'front/src/components/tags/EntityTagsSection.tsx (editable prop)']}
            donts={[
              'Add a separate pencil next to a single value: the value is the click target.',
              'Render an editable value at rest as a boxed input, select or combobox.',
              'Render a permanent add button or a permanent remove cross on a set card outside of edit mode.',
              'Keep an editor open on blur without saving: blur saves, Escape cancels.',
            ]}
          >
            <DisplayFirstEditingExample />
          </PatternSection>

          <PatternSection
            id="pattern-review-diff-then-apply"
            title="32. Review a diff, then apply"
            rule="When a change is computed rather than typed — a sync, an import — the page first lists what differs item by item, each row opening a field diff (field | this organization | source, a locale map split per locale, a set change as + / − chips, a multi-line value as a line diff). The selection is component state and closes itself over what the chosen items need, each pulled-in row saying who requires it. Review rehearses the selection on the server in a transaction that is always rolled back and shows the measured outcome in an AlertDialog; Apply runs the same function, commits, and lands on the stored record of what changed. A proposal on a free text (a knowledge note) reviews one item: an update shows a word diff of the note as it is now against the proposal; a stale proposal (the note moved since it was filed) shows three texts side by side — as the proposal saw it, as it is now, as proposed — and applies only through an explicit override; Reject asks for a reason the filer reads; the filer withdraws their own pending proposal."
            files={['front/src/components/TypesSyncPage.tsx (canonical)', 'front/src/components/ontology/FieldDiff.tsx', 'internal/handlers/types/sync_service.go (syncInTx)', 'front/src/components/knowledge/KnowledgeProposalDialog.tsx (KnowledgeProposalReviewDialog, WordDiff)', 'internal/handlers/pages/knowledge_proposals.go (ApproveProposal)']}
            donts={[
              'Preview with a client-side diff and apply something else — the review must be the server rehearsing the very apply.',
              'Pre-check items that differ: a difference may be a deliberate local edit.',
              'Show a changed item as two raw JSON panes — render FieldDiff.',
              'End on a count banner that is lost on reload — store the run and navigate to it.',
              'Apply a stale proposal over the note as it is now without showing the three texts, or reject without a reason.',
            ]}
          >
            <ReviewDiffThenApplyExample />
            <KnowledgeProposalReviewExample />
          </PatternSection>

          <PatternSection
            id="pattern-header-overflow-menu"
            title="33. Detail header — one primary action + overflow menu"
            rule="When a detail page has one thing a user comes to it to do, that action is the header's only visible button (variant default, its label hidden below md like every header button), and every other action — create a child, settings, set parent, merge, delete — goes behind an outline … trigger (aria-label « More actions ») whose menu is aligned to the end. Each entry is an icon and a label; Delete comes last, after a separator, in the destructive tone. An entry that opens a dialog opens it once the menu has closed, the dialog stays controlled by the page, and a disabled entry states why in a muted line under its label, since no tooltip shows on it. Entries the caller may not use are not rendered, and with none left the trigger is not rendered either."
            files={['front/src/components/shared/OverflowMenu.tsx', 'front/src/routes/$org/users/$userUid/index.tsx (canonical)']}
            donts={[
              'Line up five outline buttons of equal weight: nothing says which one the page is for, and they do not fit on a phone.',
              'Hand-roll the trigger or the item layout — OverflowMenu / OverflowMenuItem carry the deferred dialog opening and the disabled hint.',
              "Explain a disabled entry with a title attribute — the item has pointer-events: none and the tooltip never shows.",
              'Put the primary action in the menu too, or leave an empty … menu for a reader who may do none of it.',
            ]}
          >
            <HeaderOverflowMenuExample />
          </PatternSection>

          <PatternSection
            id="pattern-impersonation-banner"
            title="34. Impersonated session banner"
            rule="While the session's access token carries impersonated_by (a platform admin opened it through an impersonation link), a full-width warning-tone strip sits above the header on every page: « You are viewing as <name> » and an outline « End session » button that logs out. It is not dismissable and restricts nothing: the session has the user's own permissions, the banner only says whose eyes the admin is borrowing."
            files={['front/src/components/impersonation/ImpersonationBanner.tsx', 'front/src/routes/__root.tsx', 'front/src/routes/impersonate.$token.tsx']}
            donts={[
              'Let the admin dismiss it: a hidden banner is how an admin forgets they are writing as someone else.',
              'Disable buttons or hide pages on impersonated_by: the session is the user\'s, restricting it would show the admin a product the user never sees.',
              'Use the destructive tone: nothing is wrong, it is a state to keep in mind.',
            ]}
          >
            <ImpersonationBannerView name="Alice Martin" onEndSession={() => {}} />
          </PatternSection>

          <PatternSection
            id="pattern-signature-pad"
            title="35. Handwritten signature — draw or type"
            rule="A handwritten signature is drawn in a 3:1 frame (finger, stylus, mouse) with « Clear », or typed and previewed in a handwriting font, the keyboard-only alternative. Only the geometry of a drawing leaves the page (points normalized to the frame, never their time or pressure, which would be biometric data), the server re-renders the image, and fewer than two strokes is refused on both sides."
            files={['front/src/components/signatures/SignaturePad.tsx', 'front/src/components/signatures/signature-utils.ts', 'internal/signing/handwriting.go']}
            donts={[
              'Send a PNG from the browser: the server renders the image from the strokes, it never trusts an image.',
              'Record pointer timestamps, pressure or tilt.',
              'Offer drawing only: typing the name is the accessible way, required for whoever cannot draw.',
            ]}
          >
            <SignaturePadExample />
          </PatternSection>

          <PatternSection
            id="pattern-colour-by"
            title="36. Colour by — a thematic and its legend"
            rule="A plan (stacking plan, floor plan, site map) is coloured by one value chosen in a « Colorer par » selector: suggestions first, then every eligible property by category, each with its coverage, and « Aucune ». The legend sits beside it, one row per class with its count and surface, then « Non renseigné » and « Non applicable » (and « Hors de toute classe » for a stored definition, with a « Modifier » link to its editor). A row isolates its class and dims the others; a second click lifts it. The colour is stored on the value (enum value, boolean token, derived from the code for asset types and groupers), never assigned by rank. The choice lives in the URL (colour_by) and in local storage per view."
            files={['front/src/components/thematics/ThematicPicker.tsx', 'front/src/components/thematics/ThematicLegend.tsx', 'front/src/components/thematics/ThematicBar.tsx', 'internal/handlers/thematics/service.go']}
            donts={[
              'Give a colour by order of appearance: the same value must look the same on every plan.',
              'Offer a property with a single value in scope as available: it is listed greyed out, with its reason.',
              'Hard-code a status palette in a view: read the legend.',
            ]}
          >
            <ColourByExample />
          </PatternSection>

          <PatternSection
            id="pattern-typed-delete"
            title="37. Irreversible delete — impact preview and typed name"
            rule="Deleting a whole subtree opens an AlertDialog that first shows what goes with it, computed by the server (children by type, documents deleted and kept, links), then the blockers that would make it fail. The destructive button stays disabled until the item's name is typed. The request carries the typed name and the previewed count, so a subtree that changed since the preview is refused (409) and the preview reloads."
            files={['front/src/components/archive/ArchiveDeleteDialog.tsx', 'front/src/hooks/use-archive.ts', 'internal/handlers/archive/service.go']}
            donts={[
              'Count what goes on the client: the preview is the server\'s, the same code the delete runs.',
              'Enable the button on a blocker, or on a name that only starts like the item.',
              'Use this for a reversible action (archive, detach): §6 is enough there.',
            ]}
          >
            <TypedDeleteExample />
          </PatternSection>

          <PatternSection
            id="pattern-select-all-matching"
            title="38. Select all N matching"
            rule="After the page checkbox, offer « Select all N matching »: the selection then follows the filter instead of uids. Any change of filter, tab or search drops it. The destructive step is resolved by the server first (totals, blocked items left out), keeps a typed count, and sends the previewed uids in chunks of 200 with their expected count."
            files={['front/src/components/archive/ArchivedItemsPage.tsx', 'front/src/components/archive/ArchiveDeleteDialog.tsx', 'front/src/hooks/use-archive.ts']}
            donts={[
              'Send the filter to a destructive call without the count the person saw: a changed set must be refused (409).',
              'Keep « all matching » after the filter changed.',
              'Mount the dialog inside the table: it disappears with the last row.',
            ]}
          >
            <SelectAllMatchingExample />
          </PatternSection>

          <PatternSection
            id="pattern-resolution-card"
            title="39. Resolution card, title first"
            rule="A resolution is one card: its number, its one-line title, chips for the subject, the majority (art. 24) and the outcome, and the voices as a thin for / against / abstain bar. The legal clause is not on the card (see 40). The same card answers « was it voted? » on an asset's Decisions tab, with its standing as a second badge computed by the server."
            files={['front/src/components/assemblies/ResolutionsTab.tsx', 'front/src/components/assemblies/AssetDecisions.tsx', 'front/src/components/assemblies/VoteBar.tsx']}
            donts={[
              'Print the legal text as the card title: the title is one line, the clause is behind « Show text ».',
              'Spell the majority out in the chip: « art. 24 », the long name is its tooltip.',
              'Draw an empty or NaN bar when no voice is recorded: draw nothing.',
            ]}
          >
            <ResolutionCardExample />
          </PatternSection>

          <PatternSection
            id="pattern-collapsible-text"
            title="40. Collapsible long text"
            rule="A long text is closed by default behind a « Show text » link that becomes « Hide text »: the screen leads with the answer, the clause is read on demand. A paragraph that only explains why is folded into a help popover (« ? ») next to the one-line statement."
            files={['front/src/components/assemblies/CollapsibleText.tsx', 'front/src/components/assemblies/HelpHint.tsx']}
            donts={[
              'Open it by default: it is the answer\'s evidence, not the answer.',
              'Use an icon alone as the toggle: it is a text link.',
              'Stack explanatory paragraphs under a form: one line, then a help popover.',
            ]}
          >
            <CollapsibleTextExample />
          </PatternSection>

          <PatternSection
            id="pattern-step-list"
            title="41. Step list with a next action"
            rule="A phase made of several blocks is a list of steps, one open at a time, each with a number, a title, a one-line summary while closed and a status chip (done, to do, blocked). One bar above names the next action in a sentence and carries the single primary button."
            files={['front/src/components/assemblies/StepList.tsx', 'front/src/components/assemblies/ConvocationTab.tsx', 'front/src/components/assemblies/SittingTab.tsx']}
            donts={[
              'Show every block at once: the status chips are the overview.',
              'Put two primary buttons on the screen: the next action is the only one.',
              'Hide a blocked step: say what it waits on.',
            ]}
          >
            <StepListExample />
          </PatternSection>

          <PatternSection
            id="pattern-scoped-asset-search"
            title="42. Scoped asset search"
            rule="A picker that knows its context searches that place first, shows an 'In: place' chip with a 'Search everywhere' button, and never widens silently. Each row is the name followed by its path from the root, with the type as a muted second line."
            files={['front/src/components/AssetPicker.tsx', 'front/src/lib/assetPath.ts']}
            donts={[
              'Search the whole organization when the context already names a building or a floor.',
              'Widen the search on your own when the scope finds nothing: offer the button.',
              'Print a bare name: two Chambre 1 cannot be told apart.',
            ]}
          >
            <ScopedAssetSearchExample />
          </PatternSection>

          <PatternSection
            id="pattern-resizable-label-column"
            title="43. Resizable label column"
            rule="A grid or timeline whose first column holds free text of any length lets the viewer widen it: a drag handle on the right edge of the header cell (also focusable, arrows move it by 16 px, double-click resets), width clamped to 160-640 px, kept per viewer in localStorage inside try/catch. The text keeps truncate and carries its full value in title."
            files={['front/src/components/ContractsTimeline.tsx']}
            donts={[
              'Fix the column width and cut long names with no way to read them.',
              'Wrap the name on several lines: lanes get tall and uneven.',
              'Put the width in the URL or the database: it is a per-viewer convenience.',
            ]}
          >
            <div className="text-sm text-muted-foreground">See the contracts timeline (data-testid timeline-label-resize).</div>
          </PatternSection>

          <PatternSection
            id="pattern-network-layer"
            title="44. Network layer — catalogue colours, a legend, family toggles"
            rule="Building networks (ducts, pipes, cable trays) are drawn as their own primitive, never as thin walls: a coloured polyline over a white casing, the colour and dash read from the ontology's network_kind catalogue. Wherever runs are drawn, a key says which colour is which — a legend column beside the drawing, or the layer panel's rows — and the reader can hide one network or a whole family (air, water, plumbing…) without editing anything."
            files={['front/src/components/plan-editor/NetworkControls.tsx', 'front/src/components/plan-editor/LayerPanel.tsx', 'front/src/components/plan-editor/engine/render.ts', 'internal/svgplan/runs.go']}
            donts={[
              'Draw a network as walls or in one grey: superposed networks become illegible.',
              'Hard-code a network colour in the front: the catalogue is per organization.',
              'Make a hidden network an edit: visibility is a display switch, never saved.',
            ]}
          >
            <NetworkLayerExample />
          </PatternSection>

          <PatternSection
            id="pattern-viewer-layer-panel"
            title="45. Viewer layer panel — hide what you are not reading"
            rule="A read-only canvas that stacks several sources (scanned sheets under a redrawn plan) gets a collapsible layer panel beside it, closed by default and toggled from the canvas toolbar: one eye per vector layer, the networks layer opening into one eye per family and per network (swatch and run count), a master eye for the background sheets, then one row per sheet with its own eye and opacity slider, plus a 'redrawn plan only' preset. Rooms and equipment on the canvas are links to their asset. It reuses the editor panel's vocabulary (§22) without its state, applies the choice with a scoped stylesheet on the already-loaded document (never a re-fetch), defaults to everything visible and keeps the choice per asset in localStorage inside try/catch."
            files={['front/src/components/plan/CompositePlanLayerPanel.tsx', 'front/src/components/plan/composite-plan-layers.ts', 'front/src/components/plan/CompositePlanTab.tsx', 'front/src/components/AssetPlanViewer.tsx', 'front/src/components/plan/PlanCanvas.tsx']}
            donts={[
              'Re-fetch or re-render the plan server-side to hide a layer: the document already has the structure.',
              "Label a sheet with its owning asset's name: every sheet on a floor would read R+1. Use the registration's source title.",
              'Put the choice in the URL or the API: it is how one viewer looks at the plan, not what the plan is.',
              'Start with layers hidden: a new sheet must show up until the viewer turns it off.',
            ]}
          >
            <ViewerLayerPanelExample />
          </PatternSection>

          <PatternSection
            id="pattern-count-by-nature"
            title="46. Count by nature — a total and its linked parts"
            rule="A count of things that come in several natures (lots: housing, commercial, parking, cellar) reads as one wrapping line: the total first, then each nature in a fixed order (never largest first), zeros hidden, the generic « to specify » nature last. Each part links to the list on its exact type in the same scope (the subtree, the import); the total is plain text because the list cannot filter every type at once. Nothing renders when the total is 0."
            files={['front/src/components/assets/LotBreakdown.tsx', 'front/src/lib/lot-breakdown.ts']}
            donts={[
              'Show one integer for things counted on different bases: the fee is per housing lot, not per lot.',
              'Order the parts by size: the same line must read the same way on every site.',
              'Split « principal » and « annex » lots, or name housing lots « logements ».',
            ]}
          >
            <div className="rounded-md border bg-card p-4" data-testid="pattern-count-by-nature-demo">
              <LotBreakdown
                org={org}
                counts={{ unit: 2, unit_parking: 109, unit_housing: 85, unit_cellar: 6, unit_commercial: 3 }}
                scope={{ ancestor_uid: 'pattern-demo-site' }}
                testId="pattern-count-by-nature"
                showTotal
              />
            </div>
          </PatternSection>

          <PatternSection
            id="pattern-grouped-list"
            title="47. Grouped list by type"
            rule="A long list of things that each have a type (an asset's contracts) is grouped by that type: one collapsible group per type, open by default, its header giving the type name and the count, then a slot for decorations (a status, a coverage line, an add action). Groups are ordered by type name, the « not set » group last; rows sort by name inside each group. Under each short name, a muted line gives what the name leaves out: the provider, then the first other covered asset and « +N ». No pager: the whole list is grouped. With an expected-contracts checklist, the header carries a status pill (pattern 15), the coverage count, « via » chips, and one primary action with the rest behind « More actions » (pattern 33); expected types without a contract come first as empty groups, then the groups holding a red row (a follow-up ticket), and a row lists its next dates."
            files={['front/src/components/ContractTypeGroupSection.tsx', 'front/src/lib/contract-groups.ts', 'front/src/lib/contract-checklist.ts', 'front/src/components/ContractChecklistParts.tsx', 'front/src/components/AssetContracts.tsx']}
            donts={[
              'Paginate a grouped list: a group cut across two pages loses its count.',
              'Repeat the provider or the building in the name: the muted line carries them.',
              'Repeat the same rows in a second card or list on the same screen.',
            ]}
          >
            <GroupedListExample />
          </PatternSection>

          <PatternSection
            id="pattern-survey-field"
            title="48. Survey field — confirm or correct a known value"
            rule="A value someone surveys on site shows the value already known first, with one gesture to confirm it (which dates it), and an input to correct it underneath. The input follows the property type (a choice list, yes / no buttons, a date, a number) and is checked before it leaves: nothing outside the list, a number in its range, a real date. A chip says the state: confirmed, corrected, answered. A required item carries a red asterisk, personal data a warning chip."
            files={['front/src/components/campaigns/CampaignEntryField.tsx', 'front/src/lib/campaign-answer.ts']}
            donts={[
              'Hide the known value: the agent confirms faster than they type, and a confirmation dates the data.',
              'Accept a free text where the property has a list of values.',
              'Block on an empty required property: say it and let the agent finish anyway.',
            ]}
          >
            <SurveyFieldExample />
          </PatternSection>

          <PatternSection
            id="pattern-agent-run-card"
            title="49. Agent run card"
            rule="An agent run is shown where its work lands: one card per target with the Agent badge, the step the agent declared, the pending proposals and the mission's outputs. Grouped decisions (approve all, reject all, cancel) are confirmed and kept to data.write; the internals (events, API calls, LLM calls, transcript) live on the run page only."
            files={['front/src/components/agent-runs/AgentRunCard.tsx', 'front/src/components/agent-runs/AgentRunOutputs.tsx', 'front/src/components/agent-runs/agent-run-status.ts']}
            donts={[
              'Show the events or the token counts on the card: members follow the step, the run page holds the internals.',
              'Approve or reject without a confirmation that says how many proposals it touches.',
              'Write a status pill by hand: read it from AGENT_RUN_STATUS.',
            ]}
          >
            <AgentRunCardExample />
          </PatternSection>

          <PatternSection
            id="pattern-assistant-answer"
            title="50. Assistant answer"
            rule="An assistant answer that wrote something lists its changes as links to their pages and offers one confirmed undo, which reports what it reverted and what it left as is. Only relative links into the organization are clickable; any other URL reads as plain text. While the agent works, the turn shows its partial text, then « Waiting for a free agent » while it is queued."
            files={['front/src/components/assistant/AssistantMessage.tsx', 'front/src/components/assistant/AssistantPanel.tsx']}
            donts={[
              'Make an external link clickable: a planted document can make the agent print a phishing link.',
              'Undo without a confirmation, or silently overwrite a value a person changed since.',
              'Derive the changes in the frontend: they come from the turn\'s entity_logs.',
            ]}
          >
            <AssistantAnswerExample />
          </PatternSection>

          <PatternSection
            id="pattern-riser-diagram"
            title="51. Risers and the column diagram"
            rule="A vertical run (a downpipe, a dry riser, a shaft) is a point on each floor plan: a disc in its network colour with a white passage glyph, a dot where it crosses the floor, an up arrow where the column starts, a down arrow where it ends, and a junction dot where a run of its network comes off it. The column itself is its asset; the building's Plan tab, Columns mode, draws it in section through the stacked floors, dashed across the floors no plan documents, with its equipment per floor."
            files={['front/src/components/plan-editor/engine/render.ts', 'front/src/components/plan/RiserDiagram.tsx', 'front/src/components/plan/riser-diagram-layout.ts', 'internal/svgplan/runs.go']}
            donts={[
              'Draw a column as a short run on each floor: a riser is a point, and the column is its asset.',
              'Draw an undocumented floor as continuous: a gap is shown dashed, never filled in.',
              'Draw the diagram by hand: it is read off the floor plans\' risers.',
            ]}
          >
            <RiserExample />
          </PatternSection>

          <PatternSection
            id="pattern-lettability-gate"
            title="52. Lettability gate card"
            rule="A lettability verdict has three states, never a boolean: allowed (green), blocked (red: a proven breach or a missing document, listed as blockers) and not_evaluated (amber: something unknown, listed as reasons). The card always shows the jurisdiction and its source, and the pack releases that judged in a footer, marked « indicative » while no named reader signed them off. Labels come from the rules, never from the code."
            files={['front/src/components/occupation/OccupationTab.tsx', 'internal/handlers/occupancies/gate.go']}
            donts={[
              'Show not_evaluated as allowed, or merge it with blocked: both close publication, for different reasons.',
              'Name a criterion in code or translations: the rule ships its label.',
              'Hide the jurisdiction when it is unknown: say so, with links to the address and the organization setting.',
            ]}
          >
            <LettabilityGateExample />
          </PatternSection>

          <PatternSection
            id="pattern-proposed-value"
            title="53. Proposed value — not yet confirmed"
            rule="A value the product proposes from rules and nobody has confirmed yet is an amber warning badge (or an amber tinted box for a card) whose text says it is a proposal (« proposed: yes »). Once a person decides, it becomes the plain decision badge, without the word. The same treatment as an unconfirmed review clause."
            files={['front/src/components/expenses/RecoverableChip.tsx', 'front/src/components/lease/ReviewTermsCard.tsx', 'front/src/components/expenses/ExpenseRecoverableCard.tsx']}
            donts={[
              'Draw a proposal with a dashed border: a dashed border is a drop target (§11).',
              'Grey a proposal out: it must read as something to act on, not as disabled.',
              'Store the proposal: only the decision is written, the proposal is computed at read time.',
            ]}
          >
            <ProposedValueExample />
          </PatternSection>

          <PatternSection
            id="pattern-secret-once"
            title="54. Secret shown once"
            rule="A secret the server answers once (a webhook signing secret) opens in a dialog right after the action that made it: monospace and selectable, a copy button, and an amber line saying it will not be shown again. It lives in memory only and is gone when the dialog closes; a reload never brings it back, and no GET ever returns it."
            files={['front/src/components/shared/SecretReveal.tsx', 'front/src/components/webhooks/WebhooksSettingsPage.tsx']}
            donts={[
              'Store the secret in the cache, the URL or local storage so the page can show it again.',
              'Mask it behind dots with a reveal toggle: it is shown once, in full, to be copied.',
              'Let the dialog close on an outside click without the closing button saying it is stored.',
            ]}
          >
            <div className="max-w-xl" data-testid="pattern-secret-once-example">
              <SecretReveal secret="whsec_MfKQ9r8GKYqrTwjUPD8ILPZIo2LaLaSw" onceLabel="Copy it now: it will not be shown again." copyLabel="Copy" copiedLabel="Copied" />
            </div>
          </PatternSection>

          <PatternSection
            id="pattern-collapsible-sidebar-group"
            title="55. Labelled collapsible sidebar group"
            rule="A sidebar group that gathers one business area (« Gestion locative ») carries a label that is also its toggle: chevron, aria-expanded, a data-testid of sidebar-group-<id>-toggle. It is expanded by default and the collapsed state is kept per browser in localStorage (sidebar.collapsed-groups, read and written in try/catch). It renders open whenever the current route is one of its entries, so the active item is never hidden. In the icon rail the label disappears and the entries are plain icons. Groups without a label stay as they are."
            files={['front/src/components/AppSidebar.tsx', 'front/src/lib/sidebar-menus.ts']}
            donts={[
              'Store the collapsed state on the server for a purely visual preference.',
              'Hide the group that holds the current page.',
              'Nest a second toggle inside the icon rail.',
            ]}
          >
            <div className="max-w-xs rounded-lg border border-sidebar-border bg-sidebar p-2 text-sm" data-testid="pattern-collapsible-sidebar-group-example">
              <div className="flex h-8 items-center justify-between px-2 text-xs font-medium text-sidebar-foreground/70">
                Gestion locative
                <AppIcon path={mdiChevronDown} size="sm" />
              </div>
              <div className="px-2 py-1">Baux</div>
              <div className="px-2 py-1">Annonces</div>
              <div className="px-2 py-1">Candidatures</div>
            </div>
          </PatternSection>

          <PatternSection
            id="pattern-nested-components"
            title="56. Nested components in a grouped list"
            rule="In a list grouped by type (the Equipment tab by trade), an item that is part of another listed item (a part_of edge: detectors of an SSI) renders under it, indented, collapsed by default behind a « N composants » chip — whatever its own group — and never a second time in its own group. A search or filter matching a component shows its parent expanded with that component. Each row keeps its own location line: the nesting says what it counts towards, not where it is."
            files={['front/src/components/equipment/TradeAccordion.tsx', 'front/src/lib/equipment/recomposeEquipmentTab.ts', 'front/src/components/equipment/EquipmentRow.tsx']}
            donts={[
              'Re-parent the component in the tree to nest it: it would lose its floor.',
              'List the component in its own group as well as under its parent.',
              'Expand every parent by default: the chip already says how many components it holds.',
            ]}
          >
            <div className="overflow-x-auto rounded-md border" data-testid="pattern-nested-components-example">
              <EquipmentRowTree org="demo" depth={0} row={NESTED_COMPONENTS_EXAMPLE} onAddComponent={() => undefined} />
            </div>
          </PatternSection>

          <PatternSection
            id="pattern-attention-badge"
            title="57. Attention badge"
            rule="A sidebar entry where something waits on the member (new or overdue tickets, unread threads, documents to validate, contracts in their notice window) carries a red AttentionBadge: its count is the predicate of a filtered list, and the badge is a link to that list, not to the entry's plain page. The tooltip gives the breakdown (« 3 nouveaux · 2 en retard · dont 1 critique »). Collapsed to the rail, a dot on the icon replaces it and the count goes to the button's aria-label. A neutral count (unresolved senders) stays a SidebarMenuBadge and gives way to the red one. While anything waits, the tab title starts with « (N) »."
            files={['front/src/components/ui/attention-badge.tsx', 'front/src/components/NavAttention.tsx', 'front/src/lib/attention.ts', 'front/src/hooks/useAttentionCounts.ts', 'internal/handlers/attention/service.go']}
            donts={[
              'Paint a total red: red means a person is expected to act.',
              'Link the badge to the unfiltered page: the number must match the list it opens.',
              'Count anything outside the member\'s scope: the counts run under their rights, server side.',
            ]}
          >
            <TooltipProvider>
              <div className="flex flex-wrap items-start gap-6" data-testid="pattern-attention-badge-example">
                <ul className="w-56 space-y-1 rounded-md border bg-sidebar p-2 text-sm text-sidebar-foreground">
                  {[
                    { label: 'Pilotage', count: 5, tip: '3 nouveaux · 2 en retard · dont 1 critique' },
                    { label: 'Conversations', count: 4, tip: '4 conversations non lues' },
                    { label: 'Documents', count: 250, tip: '250 documents à valider' },
                  ].map((entry) => (
                    <li key={entry.label} className="relative flex h-8 items-center rounded-md px-2 hover:bg-sidebar-accent">
                      <span className="flex-1">{entry.label}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span><AttentionBadge count={entry.count} aria-label={`${entry.label} : ${entry.tip}`} /></span>
                        </TooltipTrigger>
                        <TooltipContent side="right">{entry.tip}</TooltipContent>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
                <ul className="w-12 space-y-1 rounded-md border bg-sidebar p-2 text-sidebar-foreground" aria-label="Collapsed sidebar">
                  {[SECTION_ICONS.steering, SECTION_ICONS.conversations, SECTION_ICONS.documents].map((icon, i) => (
                    <li key={i} className="relative flex size-8 items-center justify-center rounded-md">
                      <AppIcon path={icon} size="md" />
                      <AttentionDot className="absolute right-0.5 top-0.5" />
                    </li>
                  ))}
                </ul>
              </div>
            </TooltipProvider>
          </PatternSection>
        </main>
      </div>
    </div>
  )
}

const NESTED_COMPONENTS_EXAMPLE: EquipmentTabRow = {
  id: '0192f3a4-0000-7000-8000-000000000561',
  name: 'SSI catégorie A',
  type_label: 'SSI',
  location_label: 'PC sécurité',
  condition: 'good',
  maintenance_contract: { uid: '0192f3a4-0000-7000-8000-000000000564', name: 'Maintenance SSI' },
  components_expanded: true,
  components: [
    { id: '0192f3a4-0000-7000-8000-000000000562', name: 'Détecteur R+3 bureau', type_label: 'Détecteur', location_label: 'R+3', condition: 'good', maintenance_contract: { uid: '0192f3a4-0000-7000-8000-000000000564', name: 'Maintenance SSI' }, maintenance_via: 'SSI catégorie A' },
    { id: '0192f3a4-0000-7000-8000-000000000563', name: 'Déclencheur manuel hall', type_label: 'Déclencheur manuel', location_label: 'RDC', condition: 'fair', maintenance_contract: { uid: '0192f3a4-0000-7000-8000-000000000564', name: 'Maintenance SSI' }, maintenance_via: 'SSI catégorie A' },
  ],
}

const EXAMPLE_COUNTS = { asset: 0, contact: 0, contract: 0, asset_data: 0, contract_data: 0, plan_proposal: 0,
    intervention: 0, contract_value: 0, contract_asset: 0, document_link: 0, asset_relation: 0, total: 0 }

function exampleRun(overrides: Partial<AgentRun>): AgentRun {
  return {
    uid: '0192f3a4-0000-7000-8000-000000000049',
    mission_code: 'enrich-site',
    mission_version: '1',
    target_entity_type: 'asset',
    target_entity_uid: null,
    harness: 'pi',
    model: 'Qwen3.8-27B',
    provider: 'chutes',
    write_mode: 'propose',
    params: { pii_policy: 'skip' },
    status: 'succeeded',
    created_at: '2026-10-22T08:00:00Z',
    pending_proposals: EXAMPLE_COUNTS,
    ...overrides,
  }
}

const EXAMPLE_MISSION: AgentMission = {
  code: 'enrich-site',
  version: 1,
  title_key: 'agent-runs:missions.enrich-site.title',
  description_key: 'agent-runs:missions.enrich-site.description',
  targets: ['import', 'site'],
  timeout_minutes: 120,
  max_turns: 500,
  steps: ['inventory', 'audit', 'structure', 'leases', 'contracts', 'diagnostics', 'interventions', 'assemblies', 'finance', 'equipment', 'review']
    .map((code) => ({ code, title_key: `agent-runs:missions.enrich-site.steps.${code}` })),
  outputs: ['audit', 'score_after', 'score_before'],
  harnesses: [],
  params_schema: {},
}

function AgentRunCardExample() {
  const { org } = useParams({ from: '/$org/patterns' })
  const succeeded = exampleRun({
    current_step: { code: 'review', title_key: 'agent-runs:missions.enrich-site.steps.review', position: 11, steps_total: 11, status: 'done' },
    pending_proposals: { ...EXAMPLE_COUNTS, asset: 2, asset_data: 5, total: 7 },
    output: {
      score_before: 2.5,
      score_after: 7,
      audit: [
        { theme: 'structure', finding: 'Building B has no floors in the platform.', status: 'missing', page: 4, fixed_by_step: 'structure' },
        { theme: 'documents', finding: 'The insurance certificate is filed.', status: 'ok' },
      ],
    },
    summary: 'Two buildings proposed with their floors, five values read from the 2024 diagnostics.',
  })
  const running = exampleRun({
    uid: '0192f3a4-0000-7000-8000-000000000050',
    status: 'running',
    current_step: { code: 'contracts', title_key: 'agent-runs:missions.enrich-site.steps.contracts', position: 5, steps_total: 11, status: 'started', done: 12, total: 40 },
    pending_proposals: { ...EXAMPLE_COUNTS, contract: 3, total: 3 },
  })
  return (
    <div className="space-y-4" data-testid="pattern-agent-run-card-example">
      <AgentRunCard org={org} run={succeeded} mission={EXAMPLE_MISSION} canDecide canOpenDetails={false} />
      <AgentRunCard org={org} run={running} mission={EXAMPLE_MISSION} canDecide canOpenDetails={false} />
    </div>
  )
}

function SurveyFieldExample() {
  const [entries, setEntries] = useState<CampaignEntry[]>(() => {
    const item = (over: Partial<CampaignEntry['item']>): CampaignEntry['item'] => ({
      uid: 'i', kind: 'property', property_uid: null, property_code: 'x', label: { en: 'Item' }, data_type: 'text', options: [],
      multiple: false, min: null, max: null, integer_only: false, required: false, personal_data: false, ...over,
    })
    const entry = (uid: string, it: CampaignEntry['item'], known: unknown, answer: unknown = null, confirmed = false): CampaignEntry => ({
      uid, item: it, known_value: known, answer, confirmed, answered_at: null, answered_by: null, apply_status: 'pending',
    })
    return [
      entry('floors', item({ property_code: 'floors', label: { en: 'Floors' }, data_type: 'number', min: 0, max: 60, integer_only: true, required: true }), 5),
      entry('heating', item({ property_code: 'heating', label: { en: 'Heating' }, data_type: 'enum', options: [
        { value: 'gas', name: { en: 'Gas' } }, { value: 'electric', name: { en: 'Electric' } }] }), 'gas', null, true),
      entry('tenant', item({ property_code: 'tenant', label: { en: 'Caretaker name' }, personal_data: true }), null, 'J. Martin'),
      entry('roof', item({ kind: 'question', property_code: null, label: { en: 'Is the roof access secured?' }, data_type: 'boolean', required: true }), null),
    ]
  })
  return (
    <div className="max-w-xl divide-y rounded-lg border px-4" data-testid="pattern-survey-field-demo">
      {entries.map((e) => (
        <CampaignEntryField
          key={e.uid}
          entry={e}
          canFill
          testId={`pattern-survey-field-${e.uid}`}
          onSave={async (answer, confirmed) =>
            setEntries((all) => all.map((x) => (x.uid === e.uid ? { ...x, answer, confirmed } : x)))
          }
        />
      ))}
    </div>
  )
}

function NetworkLayerExample() {
  const families = [
    { family: 'Air', networks: [
      { code: 'supply_air', label: 'Supply air', color: '#2563eb', count: 82 },
      { code: 'return_air', label: 'Return air', color: '#ca8a04', count: 38 },
    ] },
    { family: 'Hydronic', networks: [
      { code: 'chilled_water_supply', label: 'Chilled water supply', color: '#0891b2', count: 40 },
      { code: 'chilled_water_return', label: 'Chilled water return', color: '#0891b2', dash: '6 4', count: 34 },
    ] },
  ]
  const [hidden, setHidden] = useState<Set<string>>(new Set(['return_air']))
  const toggle = (codes: string[], hide: boolean) => setHidden((prev) => {
    const next = new Set(prev)
    for (const c of codes) {
      if (hide) next.add(c)
      else next.delete(c)
    }
    return next
  })
  return (
    <div className="max-w-xs rounded-md border text-xs" data-testid="pattern-network-layer-demo">
      {families.map(({ family, networks }) => {
        const codes = networks.map((n) => n.code)
        const familyHidden = codes.every((c) => hidden.has(c))
        return (
          <div key={family} className="py-1">
            <button type="button" className="flex items-center gap-2 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground" onClick={() => toggle(codes, !familyHidden)}>
              {familyHidden ? 'Show' : 'Hide'} · {family}
            </button>
            {networks.map((n) => (
              <button key={n.code} type="button" className="flex w-full items-center gap-2 px-3 py-1 pl-6 text-left hover:bg-muted" onClick={() => toggle([n.code], !hidden.has(n.code))}>
                <NetworkSwatch color={n.color} dash={n.dash} />
                <span className={hidden.has(n.code) ? 'text-muted-foreground line-through' : 'text-foreground'}>{n.label}</span>
                <span className="ml-auto text-[10px] text-muted-foreground">{n.count}</span>
              </button>
            ))}
          </div>
        )
      })}
    </div>
  )
}

function ViewerLayerPanelExample() {
  const [layers, setLayers] = useState<CompositePlanLayers>(DEFAULT_LAYERS)
  return (
    <div className="w-72 rounded-lg border bg-card" data-testid="pattern-viewer-layer-panel-demo">
      <CompositePlanLayerPanel
        className="h-full"
        underlays={[
          { key: 'survey', label: 'Plan géomètre R+1' },
          { key: 'ssi', label: '[SSI / CFA] Implantation détection R+1 — RMS 0,42 m' },
          { key: 'cvc', label: '[CVC] Réseaux aérauliques R+1 — RMS 0,31 m' },
          { key: 'raw', label: '[NON RECALÉ] Faux plafond R+1' },
        ]}
        vectorLayers={['spaces', 'walls', 'openings', 'runs', 'equipment']}
        layers={layers}
        onChange={setLayers}
        networks={[
          { family: 'air', label: 'Aéraulique', networks: [
            { code: 'supply_air', label: 'Air soufflé', color: '#2563eb', count: 39 },
            { code: 'return_air', label: 'Air repris', color: '#ca8a04', count: 14 },
          ] },
          { family: 'plumbing', label: 'Plomberie', networks: [
            { code: 'cold_water', label: 'Eau froide (EF)', color: '#4f46e5', count: 4 },
          ] },
          { family: 'electrical', label: 'Électricité', networks: [
            { code: 'cable_tray', label: 'Chemin de câbles', color: '#db2777', dash: '6 3', count: 4 },
          ] },
        ]}
      />
    </div>
  )
}

function ScopedAssetSearchExample() {
  const rows = [
    { uid: 'g', name: 'Capitul' },
    { uid: 'f', name: '2e', parent_asset_uid: 'g' },
    { uid: 'a', name: 'App 222', parent_asset_uid: 'f' },
    { uid: 'r', name: 'Chambre 1', parent_asset_uid: 'a' },
  ]
  const byUid = new Map(rows.map((r) => [r.uid, r]))
  return (
    <div className="max-w-md space-y-2 text-sm" data-testid="pattern-scoped-asset-search-demo">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-full border px-2 py-0.5">In: 2e</span>
        <span className="underline">Search everywhere</span>
      </div>
      <div className="rounded-md border px-3 py-2">
        <div>{formatAssetWithPath(byUid.get('r')!, byUid)}</div>
        <div className="text-xs text-muted-foreground">Room</div>
      </div>
    </div>
  )
}

// The plan editor's own layer panel, fed a static scene document so the shared
// component itself is what the page renders (no local re-implementation).
const LAYER_PANEL_EXAMPLE_DOC: SceneDoc = {
  frame: { uid: 'pattern-demo-floor', name: 'Floor 1 (demo)', typeCode: 'floor' },
  registrationEdits: {},
  repTitles: {},
  underlays: [
    {
      key: 'u1',
      repUid: 'pattern-demo-scan',
      registrationUid: 'pattern-demo-reg-scan',
      assetUid: 'pattern-demo-floor',
      assetName: 'Floor plan scan (demo)',
      transform: { tx: 0, ty: 0, rotation_deg: 0, scale: 16000 },
      height: 1.41,
      opacity: 0.85,
      visible: true,
    },
  ],
  symbols: {},
  elements: [
    {
      key: 'w1', kind: 'wall', source: { repUid: 'r1', registrationUid: 'pattern-demo-reg-walls', transform: { tx: 0, ty: 0, rotation_deg: 0, scale: 1 }, localId: 'wall-1' },
      owner: { assetUid: 'pattern-demo-floor', assetName: 'Floor 1 (demo)', assetTypeCode: 'floor' },
      start: { x: 0, y: 0 }, end: { x: 6000, y: 0 }, thickness: 200,
    },
    {
      key: 's1', kind: 'space', source: { repUid: 'r2', transform: { tx: 0, ty: 0, rotation_deg: 0, scale: 1 }, localId: 'space-1' },
      owner: { assetUid: 'pattern-demo-room', assetName: 'Bureau (demo)', assetTypeCode: 'room' },
      name: 'Bureau (demo)', boundary: [{ x: 0, y: 0 }, { x: 6000, y: 0 }, { x: 6000, y: 4000 }],
    },
    {
      key: 'e1', kind: 'equipment', source: { transform: { tx: 0, ty: 0, rotation_deg: 0, scale: 1 }, localId: 'equipment-1' },
      owner: { newAsset: { key: 'a1', name: 'Radiator (demo)', typeCode: 'hvac_radiator', parentUid: 'pattern-demo-unit' } },
      position: { x: 1500, y: 1200 }, rotationDeg: 0, width: 1000, depth: 110, typeCode: 'hvac_radiator',
    },
  ],
}

function SceneLayerPanelExample() {
  const [layers, setLayers] = useState(defaultLayerStates())
  const [selected, setSelected] = useState<string | null>('s1')
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null)
  return (
    <div className="h-80 w-full max-w-xs overflow-hidden rounded-md border border-border">
      <LayerPanel
        doc={LAYER_PANEL_EXAMPLE_DOC}
        version={0}
        // The example doc names every element, so nothing falls back here.
        typeLabelOf={(code) => code}
        layers={layers}
        onLayerChange={(id, patch) => setLayers((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))}
        onUnderlayChange={() => {}}
        selectedKey={selected}
        onSelect={setSelected}
        unregisteredUnderlays={[]}
        onUseAsUnderlay={() => {}}
        registeringUnderlay={false}
        selectedLayerUid={selectedLayer}
        onSelectLayer={setSelectedLayer}
        onCalibrateLayer={() => {}}
      />
    </div>
  )
}

// Master-detail hierarchy browser, fed with static demo nodes so the shared
// component itself is what the page renders (no local re-implementation).
const HIERARCHY_EXAMPLE_NODES: HierarchyTreeNode[] = [
  { uid: 'admin', label: 'Administrative', parentUid: null, ordering: 0 },
  { uid: 'contracts', label: 'Contracts', parentUid: 'admin', ordering: 0, badge: 'Contract' },
  { uid: 'invoices', label: 'Invoices', parentUid: 'admin', ordering: 1, badge: 'Invoice' },
  { uid: 'technical', label: 'Technical', parentUid: null, ordering: 1 },
  { uid: 'surveys', label: 'Surveys & diagnostics', parentUid: 'technical', ordering: 0, badge: 'Survey' },
]

function HierarchyTreeExample() {
  const [selected, setSelected] = useState('contracts')
  const [editing, setEditing] = useState(false)
  const node = HIERARCHY_EXAMPLE_NODES.find(n => n.uid === selected)

  return (
    <div className="flex h-[320px] overflow-hidden rounded-lg border border-border">
      <div className="w-1/2 border-r border-border">
        <HierarchyTree
          className="h-full"
          title={`Folders (${HIERARCHY_EXAMPLE_NODES.length})`}
          nodes={HIERARCHY_EXAMPLE_NODES}
          selectedUid={selected}
          onSelect={setSelected}
          editable
          editing={editing}
          onEditingChange={setEditing}
          orderable
          onMove={() => {}}
          defaultExpanded="all"
          testIdPrefix="pattern-hierarchy"
          labels={{
            edit: 'Reorganize',
            done: 'Done',
            root: 'Root folder',
            dropToRoot: 'Drop here to move to root',
            empty: 'No folder yet',
            addChild: 'Add a subfolder',
          }}
        />
      </div>
      <div className="flex-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-foreground">{node?.label}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" aria-label="Edit" title="Edit" data-testid="pattern-hierarchy-edit">
              <AppIcon path={mdiPencil} size="sm" />
            </Button>
            <Button
              variant="destructive"
              size="icon"
              aria-label="Delete"
              title="Delete"
              data-testid="pattern-hierarchy-delete"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          The detail panel is the consumer's own component; the tree only reports the selected uid,
          which the page mirrors into <code className="font-mono text-xs">?folder_uid=</code>.
        </p>
      </div>
    </div>
  )
}

// A static, deterministic representation of the graph node + edge design.
// The interactive canvas is AssetNode inside React Flow on /$org/graph; that
// component is coupled to the React Flow store (Handle needs its context), so
// the pattern is shown here statically using the same accent-color token util.
function GraphMiniNode({
  icon,
  name,
  typeCode,
  focal,
}: {
  icon: string
  name: string
  typeCode: string
  focal?: boolean
}) {
  const accent = assetTypeColorVar(typeCode)
  return (
    <div
      className={`w-[160px] rounded-lg border bg-card text-card-foreground shadow-sm ${focal ? 'ring-2 ring-offset-2 ring-offset-background' : ''}`}
      style={{ borderColor: accent, borderLeftWidth: 4, ...(focal ? { boxShadow: `0 0 0 2px ${accent}` } : {}) }}
      data-testid={`pattern-graph-node-${focal ? 'focal' : 'neighbor'}`}
    >
      <div className="flex items-center gap-2 p-2.5">
        <AssetTypeTile type={{ code: typeCode, icon }} size="md" />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{name}</div>
          <div className="truncate text-[11px]" style={{ color: accent }}>{typeCode}</div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1 border-t border-border px-1.5 py-1 text-muted-foreground">
        <span className="rounded p-1 text-xs">＋</span>
        <span className="rounded p-1 text-xs">↗</span>
      </div>
    </div>
  )
}

function GraphExplorerExample() {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-6" data-testid="pattern-graph-explorer">
      <div className="flex items-center justify-center gap-10">
        <GraphMiniNode icon="office-building" name="Boiler Room" typeCode="building" />
        <div className="flex flex-col items-center text-muted-foreground">
          <span className="text-[11px]">attached_to</span>
          <span aria-hidden>→</span>
        </div>
        <GraphMiniNode icon="home-group" name="Site A" typeCode="building_group" focal />
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Focal node highlighted; type-hashed accent borders from theme tokens; expand / open affordances per node. Live canvas at <code>/$org/graph</code>.
      </p>
    </div>
  )
}

// Illustrative mock only — DocumentPageHighlight (the canonical component)
// fetches a real page image from the render endpoint for a specific
// document, which the patterns page's static demo data can't supply. This
// demo reproduces the same positioning technique (viewBox="0 0 1 1", boxes
// as SVG rects at normalized coordinates) over a drawn placeholder "page"
// instead of a real <img>. See the real thing live: open any extracted
// property's source badge → "view in document".
function DocumentPageHighlightExample() {
  // The real viewer opens framed on the boxes and lets you wheel/drag around;
  // this static mock reproduces what that framed state LOOKS like: the page
  // dimmed through a mask, a frame drawn *around* the box (inflated by a few
  // screen px) with a non-scaling stroke, and the interior untouched so the
  // words underneath stay at full contrast. Pad values are hardcoded here
  // because the mock has no ResizeObserver; the component derives them.
  const box = { x: 0.08, y: 0.38, w: 0.5, h: 0.035 }
  const padX = 0.013
  const padY = 0.01
  const frame = {
    x: box.x - padX,
    y: box.y - padY,
    w: box.w + 2 * padX,
    h: box.h + 2 * padY,
  }
  return (
    <div className="mx-auto max-w-xs" data-testid="pattern-document-page-highlight">
      <div className="relative overflow-hidden rounded-lg border border-border bg-card">
        <svg viewBox="0 0 300 400" className="block h-auto w-full" aria-hidden>
          <rect width="300" height="400" fill="var(--card)" />
          <rect x="24" y="28" width="160" height="12" fill="var(--muted-foreground)" opacity="0.3" />
          <rect x="24" y="56" width="252" height="7" fill="var(--muted-foreground)" opacity="0.15" />
          <rect x="24" y="72" width="252" height="7" fill="var(--muted-foreground)" opacity="0.15" />
          <rect x="24" y="88" width="180" height="7" fill="var(--muted-foreground)" opacity="0.15" />
          <rect x="24" y="140" width="90" height="8" fill="var(--muted-foreground)" opacity="0.3" />
          <rect x="24" y="160" width="150" height="12" fill="var(--foreground)" opacity="0.55" />
        </svg>
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          <defs>
            <mask id="pattern-page-highlight-mask">
              <rect x={0} y={0} width={1} height={1} fill="white" />
              <rect x={frame.x} y={frame.y} width={frame.w} height={frame.h} fill="black" />
            </mask>
          </defs>
          <rect
            x={0}
            y={0}
            width={1}
            height={1}
            fill="var(--background)"
            fillOpacity={0.35}
            mask="url(#pattern-page-highlight-mask)"
          />
          <rect
            x={frame.x}
            y={frame.y}
            width={frame.w}
            height={frame.h}
            fill="none"
            stroke="var(--primary)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            data-testid="source-highlight-box"
          />
        </svg>
        <div className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/80 p-1 backdrop-blur-sm">
          <Button variant="outline" size="icon" className="h-7 w-7" aria-label="Zoom out" title="Zoom out">
            <AppIcon path={mdiMagnifyMinusOutline} size="sm" />
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" aria-label="Zoom in" title="Zoom in">
            <AppIcon path={mdiMagnifyPlusOutline} size="sm" />
          </Button>
          <Button variant="outline" size="sm" className="h-7" aria-label="Fit page" title="Fit page">
            <AppIcon path={mdiFitToScreenOutline} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">Fit page</span>
          </Button>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Illustrative mock (see the rule for why). The frame is a real{' '}
        <code>viewBox=&quot;0 0 1 1&quot;</code> overlay with a{' '}
        <code>non-scaling-stroke</code> in screen pixels, drawn around the box rather than on it, over
        a masked dim — not a screenshot. The live viewer adds pan/zoom and opens framed on the boxes.
      </p>
    </div>
  )
}

function BulkActionsExample() {
  const rows = [
    { uid: 'a', name: 'pre_ag — Building A', type: 'Report' },
    { uid: 'b', name: 'dossier_dtg — Building A', type: 'Report' },
    { uid: 'c', name: 'reglement — Building A', type: 'Condo bylaws' },
  ]
  const [selected, setSelected] = useState<Set<string>>(new Set(['a', 'b']))
  const allSelected = selected.size === rows.length
  const toggle = (uid: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  const toggleAll = () =>
    setSelected(prev => (prev.size === rows.length ? new Set() : new Set(rows.map(r => r.uid))))

  return (
    <div className="space-y-2" data-testid="pattern-bulk-actions">
      <BulkActionsBar
        count={selected.size}
        label={`${selected.size} selected`}
        onClear={() => setSelected(new Set())}
        data-testid="pattern-bulk-bar"
      >
        <Button variant="destructive" size="sm" aria-label="Delete" title="Delete" data-testid="pattern-bulk-delete">
          <AppIcon path={mdiDelete} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">Delete</span>
        </Button>
        <Button variant="outline" size="sm" aria-label="Change type" title="Change type" data-testid="pattern-bulk-type">
          <AppIcon path={mdiSwapHorizontal} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">Change type…</span>
        </Button>
        <Button variant="outline" size="sm" aria-label="Change asset" title="Change asset" data-testid="pattern-bulk-asset">
          <AppIcon path={mdiFolderMove} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">Change asset…</span>
        </Button>
      </BulkActionsBar>

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">
                <Checkbox
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = selected.size > 0 && !allSelected }}
                  onChange={toggleAll}
                  aria-label="Select all"
                  data-testid="pattern-bulk-select-all"
                />
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Document</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Type</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.uid} className={selected.has(r.uid) ? 'bg-muted/50' : ''}>
                <td className="px-3 py-2">
                  <Checkbox
                    checked={selected.has(r.uid)}
                    onChange={() => toggle(r.uid)}
                    aria-label="Select document"
                    data-testid={`pattern-bulk-select-${r.uid}`}
                  />
                </td>
                <td className="px-3 py-2 text-foreground">{r.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function HierarchicalPickerExample({ org }: { org: string }) {
  const [value, setValue] = useState('')
  return (
    <div className="max-w-sm space-y-2">
      <p className="text-xs text-muted-foreground">
        Pick an asset type — drill down trade → sub-type, or type to search across all levels.
      </p>
      <AssetTypePicker
        org={org}
        value={value}
        onChange={setValue}
        triggerTestId="patterns-asset-type-picker"
      />
    </div>
  )
}

function StatusPillExample() {
  // Display order mirrors STATUS_PILL_CONFIG's own `order` field — the same
  // ordering used to sort rows within a compliance domain group (actionable
  // doubt first, reassurance last, NOT_APPLICABLE always trailing and muted).
  const statuses = (Object.keys(STATUS_PILL_CONFIG) as EvaluationStatus[])
    .sort((a, b) => STATUS_PILL_CONFIG[a].order - STATUS_PILL_CONFIG[b].order)
  return (
    <div className="space-y-3" data-testid="pattern-status-pill">
      <div className="flex flex-wrap items-center gap-4">
        {statuses.map((status) => (
          <div key={status} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
            <StatusPill status={status} />
            <span className="text-sm text-foreground">{status}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Hover a pill to see its i18n label tooltip. Each carries a <code>data-testid=&quot;evaluation-status-&#123;STATUS&#125;&quot;</code> for Playwright.
      </p>
    </div>
  )
}

type AssetRelation = components['schemas']['AssetRelation']

// Static demo relations block, shaped exactly like a real GET /assets/{uid}
// response's `relations` field — the canonical RelationsPanel renders it
// unmodified, same as it would for a live boiler asset. assetTypeUid is left
// undefined so the possible-relation-types fetch never fires and the example
// stays read-only.
function relationRow(
  uid: string,
  code: string,
  aggregating: boolean,
  role: { source: string; target: string },
  counterpart: { uid: string; name: string; asset_type_code: string },
  direction: 'outgoing' | 'incoming',
): AssetRelation {
  const details = {
    uid: `type-${code}`,
    code,
    name: { en: code.replace(/_/g, ' '), fr: code.replace(/_/g, ' ') },
    source_role: { en: role.source, fr: role.source },
    target_role: { en: role.target, fr: role.target },
    aggregating,
    emoji: aggregating ? '🧩' : '🔗',
    color: aggregating ? '#7C3AED' : '#0EA5E9',
  }
  const self = { uid: 'pattern-demo-boiler', name: 'Boiler (demo)', asset_type_code: 'hvac_boiler' }
  return {
    uid,
    relation_type: code,
    relation_type_details: details,
    source: direction === 'outgoing' ? self : counterpart,
    target: direction === 'outgoing' ? counterpart : self,
    created_at: '2026-01-01T00:00:00Z',
  }
}

function TypedRelationsExample({ org }: { org: string }) {
  const relations = {
    outgoing: [
      relationRow(
        'r1', 'group_member', true, { source: 'group', target: 'member' },
        { uid: 'pattern-demo-site', name: 'Site A (demo)', asset_type_code: 'building_group' },
        'outgoing',
      ),
    ],
    incoming: [
      relationRow(
        'r2', 'attached_to', false, { source: 'dependent', target: 'host' },
        { uid: 'pattern-demo-radiator', name: 'Radiator (demo)', asset_type_code: 'hvac_radiator' },
        'incoming',
      ),
      relationRow(
        'r3', 'monitors', false, { source: 'observer', target: 'observed' },
        { uid: 'pattern-demo-sensor', name: 'CO sensor (demo)', asset_type_code: 'equipment_co_alarm' },
        'incoming',
      ),
    ],
  }
  return (
    <RelationsPanel
      org={org}
      assetUid="pattern-demo-boiler"
      relations={relations}
      testIdSuffix="pattern"
    />
  )
}

function DocumentPreviewCardExample({ org }: { org: string }) {
  return (
    <div className="space-y-2">
      <DocumentPreviewCard
        org={org}
        documentUid="pattern-demo-doc-pdf"
        document={{ name: 'Fire safety report (demo).pdf', file_mime_type: 'application/pdf' }}
        testidPrefix="pattern-doc-preview-pdf"
      />
      <DocumentPreviewCard
        org={org}
        documentUid="pattern-demo-doc-image"
        document={{ name: 'Meter reading (demo).jpg', file_mime_type: 'image/jpeg' }}
        testidPrefix="pattern-doc-preview-image"
        onDetach={() => {}}
      />
    </div>
  )
}

// The canonical CategoryCards, fed static demo counts — the page renders the
// shared component, never a local copy of it. The lopsided numbers are the
// point: `data_quality` exists but is not a card (see the steering page's
// footer strip), which is what stops it out-shouting the three that matter.
function FacetCardsExample() {
  const [active, setActive] = useState<SteeringCategory>('economics')
  return (
    <CategoryCards
      active={active}
      counts={{ economics: 31, incidents: 6, compliance: 482 }}
      urgentCounts={{ economics: 4, compliance: 120 }}
      loading={false}
      onSelect={setActive}
    />
  )
}

// The real SuggestedTextInput, fed a fixed suggestion list so the reference
// page renders the shared component itself rather than a look-alike.
function SuggestedTextInputExample() {
  const [value, setValue] = useState('Vi')
  return (
    <div className="max-w-sm pb-56">
      <SuggestedTextInput
        org="demo"
        propertyDefinition={{
          uid: 'pattern-demo-brand',
          code: 'equipment_brand',
          data_type: 'text',
          properties: { suggestions: { scope: 'asset_type' } },
        } as unknown as PropertyDefinition}
        value={value}
        onChange={setValue}
        autoFocus={false}
        defaultOpen
        staticSuggestions={[
          { value: 'Viessmann', count: 12 },
          { value: 'Vaillant', count: 5 },
        ]}
        data-testid="pattern-suggested-text-input"
      />
    </div>
  )
}


// §25 — Comparison matrix. Static demo data shaped exactly like the roles page's:
// rows are resources, columns are roles, cells are a token plus one badge per
// narrowed grant.
const COMPARISON_ROWS = [
  {
    resource: 'Assets',
    blurb: 'Buildings, equipment, plans, labels and their relations',
    cells: [
      { token: 'RW', badges: [], codes: 'assets.read, assets.write' },
      { token: 'R', badges: [], codes: 'assets.read' },
      { token: 'R', badges: [], codes: 'assets.read' },
    ],
  },
  {
    resource: 'Documents',
    blurb: 'Files, folders and document links',
    cells: [
      { token: 'RW', badges: [], codes: 'documents.read, documents.write' },
      { token: 'R', badges: [], codes: 'documents.read' },
      { token: null, badges: ['shared'], codes: 'documents.read_shared' },
    ],
  },
  {
    resource: 'Issues',
    blurb: 'Reported problems and their follow-up',
    cells: [
      { token: 'RW', badges: [], codes: 'issues.read, issues.write' },
      { token: 'R', badges: [], codes: 'issues.read' },
      {
        token: null,
        badges: ['shared', 'own', 'own (write)'],
        codes: 'issues.read_shared, issues.read_own, issues.write_own',
      },
    ],
  },
  {
    resource: 'Members and roles',
    blurb: 'Organization members, invitations and roles',
    cells: [
      { token: 'RW', badges: [], codes: 'members.read, members.write' },
      { token: null, badges: [], codes: '' },
      { token: null, badges: [], codes: '' },
    ],
  },
] as const

function ComparisonMatrixExample() {
  const columns = ['Admin', 'Viewer', 'Resident']

  return (
    <div className="space-y-2" data-testid="pattern-comparison-matrix">
      <div className="overflow-x-auto border border-border rounded-lg bg-card">
        <table className="text-sm min-w-max">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-2 px-3 font-medium text-muted-foreground sticky left-0 bg-card z-10">
                Resource
              </th>
              {columns.map((column) => (
                <th key={column} className="py-2 px-3 font-medium text-center align-bottom">
                  <a href="#pattern-comparison-matrix" className="hover:underline">
                    {column}
                  </a>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map((row) => (
              <tr key={row.resource} className="border-b border-border last:border-b-0">
                <td className="py-2 px-3 sticky left-0 bg-card z-10 min-w-56 max-w-72">
                  <div className="font-medium text-foreground">{row.resource}</div>
                  <div className="text-xs text-muted-foreground">{row.blurb}</div>
                </td>
                {row.cells.map((cell, index) => (
                  <td
                    key={columns[index]}
                    className="py-2 px-3 text-center align-middle"
                    title={cell.codes || undefined}
                  >
                    {!cell.token && cell.badges.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap items-center justify-center gap-1">
                        {cell.token && (
                          <span className="font-mono font-medium text-foreground">
                            {cell.token}
                          </span>
                        )}
                        {cell.badges.map((badge) => (
                          <span
                            key={badge}
                            className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="text-xs text-muted-foreground space-y-0.5">
        <li>— nothing on this resource</li>
        <li>R — reads it</li>
        <li>RW — reads and changes it</li>
        <li>A badge means the role only sees part of the rows — hover it for the codes.</li>
      </ul>
    </div>
  )
}

// A static cohort for the distribution strip: nine peers, one outlier above the
// fence, one peer below the band.
function DistributionStripExample() {
  const peers = [88, 96, 97, 99, 101, 102, 104, 106, 109, 121]
  const format = (v: number) => `${Math.round(v)} € / unit`
  const rows = [
    { label: 'Above the fence', value: 312, outlier: 'high', reading: '> D9 · +206 %' },
    { label: 'In the pack', value: 102, outlier: null, reading: 'D6 · +0 %' },
  ]
  return (
    <div className="space-y-2" data-testid="pattern-distribution-strip-demo">
      {rows.map((row) => (
        <div key={row.label} className="grid grid-cols-[10rem_minmax(0,1fr)_8rem] items-center gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">{row.label}</div>
            <div className="text-[11px] text-muted-foreground">n = 11</div>
          </div>
          <DistributionStrip
            value={row.value}
            stats={{ p10: 96, p25: 98, p50: 102, p75: 106, p90: 121, fence_high: 118, fence_low: 86, fence_available: true }}
            peerValues={[...peers, row.value].sort((a, b) => a - b)}
            outlier={row.outlier}
            label={row.label}
            formatValue={format}
            testId={`pattern-distribution-strip-${row.outlier ? 'outlier' : 'pack'}`}
          />
          <span className={`text-right text-sm tabular-nums ${row.outlier ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{row.reading}</span>
        </div>
      ))}
    </div>
  )
}

// §31 — Click-to-edit values, pencil for sets (specs 2026-09-19-01 and
// 2026-10-11-29). Two miniatures: a single value (title-like, click to edit)
// and a card of linked entities (stakeholders-like, one pencil / Done). Demo-only local state, same shape as
// ContractPage.tsx's own handlers.
function DisplayFirstEditingExample() {
  const [fieldValue, setFieldValue] = useState('Cleaning contract — Building A')
  const [editingField, setEditingField] = useState(false)
  const [fieldDraft, setFieldDraft] = useState(fieldValue)

  const settled = useRef(false)
  const saveField = () => {
    if (settled.current) return
    settled.current = true
    setFieldValue(fieldDraft)
    setEditingField(false)
  }
  const cancelField = () => {
    settled.current = true
    setFieldDraft(fieldValue)
    setEditingField(false)
  }

  const [cardEditing, setCardEditing] = useState(false)
  const [rows, setRows] = useState(['Total Cleaning SAS'])

  return (
    <div className="space-y-6" data-testid="pattern-display-first-editing-demo">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">Title</h3>
        {editingField ? (
          <Input
            autoFocus
            value={fieldDraft}
            onChange={(e) => setFieldDraft(e.target.value)}
            onBlur={saveField}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveField()
              if (e.key === 'Escape') cancelField()
            }}
            className="max-w-sm"
            data-testid="pattern-display-first-field-input"
          />
        ) : (
          <EditableValue
            canEdit
            onEdit={() => { settled.current = false; setFieldDraft(fieldValue); setEditingField(true) }}
            title="Click to edit"
            placeholder="Click to edit"
            className="text-base text-foreground"
            data-testid="pattern-display-first-field-display"
          >
            {fieldValue}
          </EditableValue>
        )}
      </div>

      <div className="max-w-sm rounded-lg border border-border p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Stakeholders</h3>
          {cardEditing ? (
            <Button variant="outline" size="sm" onClick={() => setCardEditing(false)} data-testid="pattern-display-first-card-done">
              <AppIcon path={mdiCheck} size="sm" className="mr-1" />
              Done
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCardEditing(true)}
              aria-label="Edit"
              title="Edit"
              data-testid="pattern-display-first-card-edit"
            >
              <AppIcon path={mdiPencil} size="sm" />
            </Button>
          )}
        </div>
        {cardEditing && (
          <div className="mb-2 rounded border border-dashed border-border p-2 text-xs text-muted-foreground" data-testid="pattern-display-first-card-add-row">
            + add row (type, user search) — demo only
          </div>
        )}
        <ul className="space-y-1">
          {rows.map((row) => (
            <li key={row} className="flex items-center justify-between rounded border border-border px-2 py-1 text-sm">
              <span>{row}</span>
              {cardEditing && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRows((prev) => prev.filter((r) => r !== row))}
                  aria-label="Remove"
                  title="Remove"
                  data-testid={`pattern-display-first-card-remove-${row}`}
                >
                  <AppIcon path={mdiClose} size="xs" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// §32 — the sync page's row and summary bar with static data; the field diff
// is the canonical FieldDiff itself.
// §33 — the contact page's header, fed static data: the primary action and
// the shared « … » menu. Open the menu to see the entries; the last one is
// disabled with its reason, as on a contact that still has children.
function HeaderOverflowMenuExample() {
  return (
    <PageHeader
      icon={SECTION_ICONS.users}
      title="Camille Lefebvre"
      actions={
        <>
          <Button aria-label="Send a message" title="Send a message" data-testid="pattern-overflow-primary">
            <AppIcon path={SECTION_ICONS.conversations} size="xs" className="md:mr-1" />
            <span className="hidden md:inline">Send a message</span>
          </Button>
          <OverflowMenu testId="pattern-overflow-trigger" label="More actions">
            <OverflowMenuItem icon={mdiBaby} label="Create child contact" testId="pattern-overflow-create-child" />
            <OverflowMenuItem icon={mdiLan} label="Set parent" testId="pattern-overflow-set-parent" />
            <OverflowMenuItem icon={mdiSourceMerge} label="Merge into" testId="pattern-overflow-merge" />
            <OverflowMenuSeparator />
            <OverflowMenuItem
              icon={mdiTrashCanOutline}
              label="Delete"
              hint="Move or delete its child contacts first."
              destructive
              disabled
              testId="pattern-overflow-delete"
            />
          </OverflowMenu>
        </>
      }
    />
  )
}

function ReviewDiffThenApplyExample() {
  const [open, setOpen] = useState(true)
  const [checked, setChecked] = useState(true)
  return (
    <div className="space-y-3" data-testid="pattern-review-diff-then-apply-demo">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm">
        <div className="flex flex-wrap gap-3">
          <span className="text-success">12 new</span>
          <span className="text-warning">1 different</span>
          <span className="text-muted-foreground">4 only here</span>
          <span className="font-medium">{checked ? '1 selected (+2 required)' : '0 selected'}</span>
        </div>
        <Button size="sm" disabled={!checked} data-testid="pattern-review-diff-review-btn">
          {checked ? 'Review 3 changes' : 'Review 0 changes'}
        </Button>
      </div>
      <div className="rounded-lg border px-3 py-2">
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-border"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              aria-label="Select Room"
              data-testid="pattern-review-diff-row-checkbox"
            />
            <span className="text-sm font-medium">Room</span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">room</code>
            <Badge variant="warning">Differs</Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? 'Hide the changes' : 'Show the changes'}
            title={open ? 'Hide the changes' : 'Show the changes'}
            data-testid="pattern-review-diff-row-toggle"
          >
            <AppIcon path={mdiChevronDown} size="sm" className={open ? '' : '-rotate-90'} />
          </Button>
        </div>
        {open && (
          <div className="mt-2 rounded-md bg-muted/30 p-2">
            <FieldDiff
              beforeLabel="This organization"
              afterLabel="Template"
              changes={[
                { path: 'name', before: { en: 'Room', fr: 'Pièce locale' }, after: { en: 'Room', fr: 'Pièce' } },
                { path: 'possible_parent_codes', before: ['floor'], after: ['floor', 'outdoor'], added: ['outdoor'], removed: [] },
                { path: 'associated_properties.room_kind', before: { requirement: 'mandatory' }, after: null },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// A knowledge proposal under review (spec 2026-11-01-01): the word diff of
// an update, the three texts of a stale one, reject with a reason, withdraw.
function KnowledgeProposalReviewExample() {
  const seen = 'Boxing a parking space is forbidden.'
  const now = 'Boxing a parking space is forbidden (RCP art. 14).'
  const proposed = 'Boxing a parking space needs the assembly vote.'
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  return (
    <div className="mt-4 space-y-3 rounded-lg border p-3" data-testid="pattern-knowledge-proposal-review-demo">
      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">Changes to the note</div>
        <WordDiff before={now} after={proposed} />
      </div>
      <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-2" data-testid="pattern-knowledge-proposal-stale">
        <p className="text-sm">The note changed since this proposal was filed.</p>
        <div className="grid gap-2 md:grid-cols-3">
          {[
            ['As the proposal saw it', seen],
            ['As it is now', now],
            ['Proposed', proposed],
          ].map(([label, text]) => (
            <div key={label}>
              <div className="text-xs text-muted-foreground">{label}</div>
              <p className="whitespace-pre-wrap text-sm">{text}</p>
            </div>
          ))}
        </div>
      </div>
      {rejecting && (
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why it is rejected"
          aria-label="Rejection reason"
          data-testid="pattern-knowledge-proposal-reject-reason"
        />
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" data-testid="pattern-knowledge-proposal-withdraw">
          Withdraw
        </Button>
        {rejecting ? (
          <Button variant="destructive" size="sm" disabled={!reason.trim()} data-testid="pattern-knowledge-proposal-reject-confirm">
            Reject
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setRejecting(true)} data-testid="pattern-knowledge-proposal-reject">
            Reject…
          </Button>
        )}
        <Button size="sm" data-testid="pattern-knowledge-proposal-apply">
          Apply anyway
        </Button>
      </div>
    </div>
  )
}

// The signer's handwritten signature (spec 2026-10-05-05), live: draw or type.
function ColourByExample() {
  const [isolated, setIsolated] = useState<string | null>(null)
  return (
    <div className="max-w-xl" data-testid="pattern-colour-by-demo">
      <ThematicLegend
        isolated={isolated}
        onToggleIsolated={(v) => setIsolated((c) => (c === v ? null : v))}
        legend={{
          classes: [
            { value: 'A', label: { en: 'A - Very economical' }, colour: '#009a44', count: 4, surface: 210, asset_uids: [] },
            { value: 'D', label: { en: 'D - Moderately economical' }, colour: '#f7c318', count: 2, surface: 95, asset_uids: [] },
          ],
          unset: { count: 1, surface: 40, asset_uids: [] },
          not_applicable: { count: 0, surface: 0, asset_uids: [] },
        }}
      />
    </div>
  )
}

function TypedDeleteExample() {
  const [typed, setTyped] = useState('')
  const name = 'Old warehouse'
  return (
    <div className="max-w-md space-y-3 rounded-lg border border-border p-4 text-sm" data-testid="pattern-typed-delete-demo">
      <div className="font-medium">2 child assets</div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">Documents deleted</dt>
        <dd className="text-right tabular-nums">3</dd>
        <dt className="text-muted-foreground">Documents kept (also filed outside)</dt>
        <dd className="text-right tabular-nums">1</dd>
      </dl>
      <label className="block space-y-1">
        <span>Type {name} to confirm</span>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="pattern-typed-delete-input" />
      </label>
      <Button variant="destructive" disabled={!confirmMatches(typed, name, '00000000-0000-0000-0000-000000000000')} data-testid="pattern-typed-delete-btn">
        Delete for good
      </Button>
    </div>
  )
}

function SignaturePadExample() {
  const [value, setValue] = useState<Handwriting | null>(null)
  return (
    <div className="max-w-xl space-y-2" data-testid="pattern-signature-pad-demo">
      <SignaturePad value={value} onChange={setValue} />
      <p className="text-xs text-muted-foreground">{isHandwritingReady(value) ? 'Ready to sign' : 'Not ready'}</p>
    </div>
  )
}

function SelectAllMatchingExample() {
  const [all, setAll] = useState(false)
  return (
    <div className="max-w-md space-y-2 rounded-lg border border-border p-4 text-sm" data-testid="pattern-select-all-matching-demo">
      <div>{all ? '37 selected' : '20 selected'}</div>
      {all ? (
        <div>
          All 37 matching items are selected.{' '}
          <button type="button" className="font-medium underline" onClick={() => setAll(false)} data-testid="pattern-select-all-matching-clear">
            Clear selection
          </button>
        </div>
      ) : (
        <div>
          All 20 items on this page are selected.{' '}
          <button type="button" className="font-medium underline" onClick={() => setAll(true)} data-testid="pattern-select-all-matching-btn">
            Select all 37 matching
          </button>
        </div>
      )}
    </div>
  )
}

// §39 — a resolution card with static data: the real VoteBar, chips and the
// § 40 disclosure.
function ResolutionCardExample() {
  return (
    <div className="max-w-2xl rounded-xl border bg-card p-4" data-testid="pattern-resolution-card-demo">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">3</span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="font-medium">Façade renovation</div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Works</span>
            <span className="rounded-md border px-2 py-0.5">art. 25</span>
          </div>
          <CollapsibleText
            text="The general assembly decides to carry out the renovation of the street facade, for a budget of EUR 240,000 including VAT."
            testId="pattern-resolution-card-text"
          />
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="rounded-md border border-success/25 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">Adopted</span>
          <VoteBar voicesFor={620} voicesAgainst={180} voicesAbstain={40} testId="pattern-resolution-card-bar" />
        </div>
      </div>
    </div>
  )
}

// §40 — the disclosure and the help popover, closed by default.
function CollapsibleTextExample() {
  return (
    <div className="max-w-xl space-y-3 text-sm" data-testid="pattern-collapsible-text-demo">
      <p className="flex items-center gap-1">
        Mail votes close on March 8.
        <HelpHint testId="pattern-collapsible-text-help">A form may be received until the third full day before the meeting.</HelpHint>
      </p>
      <CollapsibleText text={'First paragraph of the legal text.\n\nSecond paragraph, with its line break kept.'} testId="pattern-collapsible-text-body" />
    </div>
  )
}

// §41 — a step list with its next action, controlled like the real tabs.
function StepListExample() {
  const [open, setOpen] = useState<string | undefined>('votes')
  const steps: StepDef[] = [
    { id: 'attendance', title: 'Attendance and bureau', status: 'done', summary: '8 / 8 present · chair: A. Martin', content: <p className="text-sm">The attendance list.</p> },
    { id: 'votes', title: 'Votes', status: 'todo', summary: '2 / 5 votes closed', content: <p className="text-sm">The votes, one row each.</p> },
    { id: 'closing', title: 'Minutes and closing', status: 'blocked', summary: 'Generate the minutes once every vote is closed.', content: <p className="text-sm">The minutes and the signatures.</p> },
  ]
  return (
    <div className="max-w-2xl space-y-3" data-testid="pattern-step-list-demo">
      <NextAction label="Next action" hint="Vote 3 is next: Façade renovation" testId="pattern-step-list-next">
        <Button>Open the vote</Button>
      </NextAction>
      <StepList steps={steps} open={open} onOpenChange={setOpen} testId="pattern-step-list-steps" />
    </div>
  )
}

function GroupedListExample() {
  const groups = [
    { code: 'CLEANING', title: 'Cleaning services', status: 'missing' as const, rows: [], note: 'No contract' },
    { code: 'WORK_MAINTENANCE_ELEVATOR', title: 'Elevator maintenance', status: 'covered' as const, via: 'via AFUL Les Prés', rows: [
      { name: 'Ascenseur', line: 'BOUVIER · Building B', dates: 'Next visit on 30/10/2026 · Ends on 20/10/2027', red: 'Visit overdue +1' },
      { name: 'Avenant n°2 – Ascenseur', line: 'BOUVIER', dates: 'Ends on 20/10/2027' },
    ] },
    { code: 'WORK_MAINTENANCE_PLUMBING', title: 'Plumbing and lifting pumps', status: 'partial' as const, note: 'Not covered: Building C', rows: [
      { name: 'Pompe de relevage', line: 'HYDRO SERVICES · Building B +2', dates: 'Unknown visit frequency' },
    ] },
    { code: 'none', title: 'Service not set', rows: [{ name: 'Contrat cadre', line: '' }] },
  ]
  return (
    <div className="max-w-2xl overflow-hidden rounded-md border bg-card" data-testid="pattern-grouped-list-demo">
      <p className="px-4 py-2 text-sm font-medium" data-testid="pattern-grouped-list-summary">1 service missing · 1 partial · 1 covered</p>
      {groups.map((g) => (
        <ContractTypeGroupSection
          key={g.code}
          typeCode={`pattern-${g.code}`}
          title={g.title}
          countLabel={`${g.rows.length} ${g.rows.length === 1 ? 'contract' : 'contracts'}`}
          toggleLabel={`Fold or unfold ${g.title}`}
          headerSlot={'status' in g && g.status ? (
            <>
              <ChecklistStatusPill typeCode={`pattern-${g.code}`} status={g.status} />
              {'via' in g && g.via && <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">{g.via}</span>}
              {g.status !== 'covered' && (
                <>
                  <Button size="sm" className="h-8">Upload the contract</Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" aria-label="More actions">…</Button>
                </>
              )}
            </>
          ) : undefined}
        >
          {'note' in g && g.note && <p className="px-4 py-2 text-xs text-muted-foreground">{g.note}</p>}
          {g.rows.length > 0 && (
            <ul className="divide-y divide-border">
              {g.rows.map((r) => (
                <li key={r.name} className={`px-4 py-3 ${'red' in r && r.red ? 'bg-destructive/10' : ''}`}>
                  <div className="text-sm font-medium text-foreground">{r.name}</div>
                  {r.line && <div className="text-xs text-muted-foreground">{r.line}</div>}
                  {'red' in r && r.red && <div className="text-xs font-medium text-destructive">{r.red}</div>}
                  {'dates' in r && r.dates && <div className="text-xs text-muted-foreground">{r.dates}</div>}
                </li>
              ))}
            </ul>
          )}
        </ContractTypeGroupSection>
      ))}
    </div>
  )
}

function AssistantAnswerExample() {
  const { org } = useParams({ from: '/$org/patterns' })
  const base = {
    context: {}, route: null, intent_label: null, router_usage: null, turn_started_at: '2026-10-22T08:00:00Z',
    turn_finished_at: '2026-10-22T08:00:40Z', undone_at: null, created_at: '2026-10-22T08:00:00Z',
  }
  const answer: AssistantMessageType = {
    ...base,
    uid: '0192f3a4-0000-7000-8000-000000000050',
    role: 'assistant',
    text: 'J\'ai créé la cave **Cave 12** au 1er étage et renseigné sa surface. Voir [Cave 12](/' + org + '/assets/0192f3a4-0000-7000-8000-000000000051).',
    agent_run_uid: '0192f3a4-0000-7000-8000-000000000049',
    changes: [
      { entity_table: 'assets', entity_uid: '0192f3a4-0000-7000-8000-000000000051', operation: 'INSERT',
        link: { type: 'asset', uid: '0192f3a4-0000-7000-8000-000000000051', label: 'Cave 12' } },
      { entity_table: 'asset_data', entity_uid: '0192f3a4-0000-7000-8000-000000000052', operation: 'INSERT',
        link: { type: 'asset', uid: '0192f3a4-0000-7000-8000-000000000051', label: 'Cave 12 · surface' } },
    ],
    can_undo: true,
  }
  const report: AssistantMessageType = {
    ...base,
    uid: '0192f3a4-0000-7000-8000-000000000053',
    role: 'system',
    text: 'undo',
    agent_run_uid: null,
    context: {
      code: 'undo', answer_uid: '0192f3a4-0000-7000-8000-000000000054',
      reverted: [{ entity_table: 'assets', entity_uid: '0192f3a4-0000-7000-8000-000000000055', operation: 'INSERT' }],
      skipped: [{ entity_table: 'assets', entity_uid: '0192f3a4-0000-7000-8000-000000000056', reason: 'changed_since' }],
    },
  }
  return (
    <div className="max-w-md space-y-4 rounded-lg border p-4" data-testid="pattern-assistant-answer-example">
      <AssistantMessageView org={org} message={answer} canOpenRun onUndo={() => undefined} />
      <AssistantLiveTurn text="Je cherche l'étage, puis je crée la cave." runStatus="running" />
      <AssistantLiveTurn text="" runStatus="queued" />
      <AssistantMessageView org={org} message={report} />
    </div>
  )
}

/** §51: the three passage glyphs as the plan draws them, and a column diagram fed static data. */
function RiserExample() {
  const { org } = useParams({ from: '/$org/patterns' })
  const glyph = (passage: string, junction = false) =>
    riserMarkup({ position: { x: 20, y: 20 }, passage }, { color: '#b91c1c' }, 9, 1, junction ? [{ x: 34, y: 20 }] : [])
  const level = (uid: string, name: string) => ({ asset_uid: uid, name, floor_number: null, ceiling_height_m: null, building_transform: null })
  const riser = (levelUid: string, passage: 'origin' | 'through' | 'terminal') => ({
    level_uid: levelUid, equipment: [],
    riser: { representation_uid: 'rep', id: 'k', network: 'dry_riser', at: { x: 0, y: 0 }, passage, passage_derived: false },
  })
  const diagram: RiserDiagramPayload = {
    levels: [level('l0', 'RDC'), level('l1', 'R+1'), level('l2', 'R+2'), level('l3', 'R+3')],
    columns: [
      {
        asset_uid: 'col-ep', name: 'EP 2', type_code: 'roof_rainwater', network: 'rainwater', networks: [], compliance_status: null, x: 2000, gaps: [],
        levels: [riser('l0', 'origin'), riser('l1', 'through'), riser('l2', 'through'), riser('l3', 'terminal')].map((l) => ({ ...l, riser: { ...l.riser, network: 'rainwater' } })),
      },
      {
        asset_uid: 'col-cs', name: 'Colonne sèche', type_code: 'fire_riser', network: 'dry_riser', networks: [], compliance_status: 'compliant', x: 9000, gaps: ['l2'],
        levels: [
          { ...riser('l0', 'origin'), equipment: [{ uid: 'eq-inlet', name: 'Raccord pompiers', type_code: 'fire_landing_valve' }] },
          { ...riser('l1', 'through'), equipment: [{ uid: 'eq-r1', name: 'Prise R+1', type_code: 'fire_landing_valve' }] },
          riser('l3', 'terminal'),
        ],
      },
    ],
    networks: [
      { code: 'rainwater', name: { en: 'Rainwater', fr: 'Eaux pluviales (EP)' }, color: '#0284c7', dash: '2 4', family: 'plumbing', ordering: 0 },
      { code: 'dry_riser', name: { en: 'Dry riser', fr: 'Colonne sèche' }, color: '#b91c1c', family: 'fire', ordering: 1 },
    ],
  }
  return (
    <div className="space-y-4" data-testid="pattern-riser-diagram-demo">
      <div className="flex items-center gap-6 text-xs text-muted-foreground">
        {(['through', 'origin', 'terminal'] as const).map((p) => (
          <span key={p} className="flex items-center gap-2">
            <svg width="40" height="40" viewBox="0 0 40 40" dangerouslySetInnerHTML={{ __html: glyph(p) }} />
            {p}
          </span>
        ))}
        <span className="flex items-center gap-2">
          <svg width="40" height="40" viewBox="0 0 40 40" dangerouslySetInnerHTML={{ __html: '<line x1="34" y1="20" x2="40" y2="20" stroke="#b91c1c" stroke-width="2.5"/>' + glyph('through', true) }} />
          junction
        </span>
      </div>
      <div className="max-w-xl">
        <RiserDiagramView org={org} diagram={diagram} />
      </div>
    </div>
  )
}

/** §52: the gate card in its three states, fed static data. */
function ProposedValueExample() {
  const rule = { uid: '0192f3a4-0000-7000-8000-000000000101' }
  const examples: ListedExpense[] = [
    { uid: 'decided', recoverable: true },
    { uid: 'proposed-yes', recoverable: null, recoverable_proposal: { status: 'proposed', recoverable: true, rule } as ListedExpense['recoverable_proposal'] },
    { uid: 'proposed-partial', recoverable: null, recoverable_proposal: { status: 'proposed', recoverable: true, recoverable_ratio: 0.4, rule } as ListedExpense['recoverable_proposal'] },
  ]
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="pattern-proposed-value-example">
      {examples.map((e) => <RecoverableChip key={e.uid} expense={e} />)}
    </div>
  )
}

function LettabilityGateExample() {
  const { org } = useParams({ from: '/$org/patterns' })
  const base = {
    asset_uid: '0192f3a4-0000-7000-8000-000000000052', at_date: '2026-10-25',
    jurisdiction: { code: 'FR', source: 'ancestor_address', chain: ['FR', '*'] },
    criteria: [], reasons: [], blockers: [],
    pack_releases: [{ uid: '0192f3a4-0000-7000-8000-000000000053', jurisdiction: 'FR', code: 'habitability', version: '2026.1', validated: false }],
  } satisfies Omit<Lettability, 'status'>
  const examples: Lettability[] = [
    { ...base, status: 'allowed' },
    { ...base, status: 'blocked', blockers: [{ kind: 'habitability', code: 'FR_DECENCY_ENERGY_CLASS_G', status: 'NON_COMPLIANT', label: { en: 'Energy class G is indecent (2025)', fr: 'Classe G indécente (2025)' } }] },
    { ...base, status: 'not_evaluated', reasons: [{ code: 'letting_regime_unknown' }], jurisdiction: { code: '', source: 'none', chain: ['*'] } },
  ]
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="pattern-lettability-gate-example">
      {examples.map((g) => <LettabilityGateCard key={g.status} org={org} assetUid={g.asset_uid} gate={g} />)}
    </div>
  )
}
